// The meta server's HTTP API (deployment.md §7), as the landing page uses
// it. Everything here is same-origin: nginx serves the client bundle and
// proxies `/api/` to `aquametad`, so the client needs no baked
// configuration and no CORS. The session is an HttpOnly cookie the browser
// carries by itself; `credentials: 'include'` is what makes it travel.
//
// Every failure arrives as the one error shape the meta server writes
// (R143): `{"error": {"code", "message"}}`. This module turns that into an
// `ApiError` the caller can switch on, and it turns "there is no meta
// server behind this origin" into an `ApiError` with `unavailable` set —
// that is the signal main.ts falls back to the legacy direct mode on.

import { cutRunes } from '../util/text';
import { base64Of } from '../util/base64';
import { oneOf } from '../util/oneof';
import { LOCALE, api as S } from '../gen/strings';
import {
  PLAYER_REPORT_FLEET_IDS_MAX, PLAYER_REPORT_LOG_TAIL_MAX_RUNES, PLAYER_REPORT_SCREENSHOT_MAX_BYTES,
  PLAYER_REPORT_TEXT_MAX_RUNES, PLAYER_REPORT_TOPICS, REPORT_REASONS, type ThemeCategory,
} from '../gen/balance';
import type { ChronicleFact } from '../gen/chronicle';
import { rememberEntitlements, type CachedTheme } from '../ui/lookcache';
import { reportSignal } from './fairplay';
import { num, str, strList } from './json';
import { isTimeoutError, request, requestText } from './request';

/** The code an ApiError carries when nothing answered with JSON at all. */
export const CODE_UNAVAILABLE = 'api_unavailable';

/**
 * The code an ApiError carries when the last attempt of the call waited
 * too long (R1203, R1706): `API_TIMEOUT_MS` for a call that is not safe,
 * the end of `REQUEST_BUDGET_MS` for a GET. It is NOT `unavailable`: a slow meta server is
 * still a meta server, and the entry must not take it for a page with no
 * meta server behind it and connect direct.
 */
export const CODE_TIMEOUT = 'api_timeout';

/** The code an ApiError carries when JSON came back in the wrong shape. */
export const CODE_UNEXPECTED = 'unexpected_response';

/**
 * The meta server's own error codes, from the generated `gen/balance.ts` —
 * the same `data/contracts/api.toml` that writes `httpapi/errors.go`, so
 * adding one is one wire change and not two (R341). The whole vocabulary
 * stands in the generated file; this module re-exports what a screen
 * switches on, so no import site changed.
 *
 * The seven below are the landing screen's. `unauthorized` and
 * `rate_limited` come from every listener. `invalid_credentials` is
 * deliberately the one answer for "no such account" and "the wrong
 * password", so a login route is not a way to find out who has an account
 * here; `email_taken` from the register route is what tells the screen the
 * two cases apart, after the visitor has already proved nothing. The three
 * name codes belong to `POST /api/me/name` and the two sign-in routes
 * (R226): a player name is unique, case-insensitively.
 */
export {
  CODE_UNAUTHORIZED, CODE_RATE_LIMITED,
  CODE_INVALID_LOGIN, CODE_EMAIL_TAKEN,
  CODE_NAME_TAKEN, CODE_INVALID_NAME, CODE_RENAME_SPENT,
} from '../gen/balance';

/**
 * The refusals of the two sign-in providers (R621).
 *
 * A callback answers a redirect and never JSON, so these codes reach the
 * client on the page address as `?signin=<code>` and not in an `ApiError`.
 * `already_registered` is the one code that arrives both ways: on the page
 * address when a provider identity of that kind is already on the account,
 * and from `POST /api/auth/register` when the account already holds a
 * password.
 */
export {
  CODE_ALREADY_LINKED, CODE_IDENTITY_TAKEN,
  CODE_SIGN_IN_STATE, CODE_SIGN_IN_REFUSED, CODE_SIGN_IN_METHOD_OFF,
} from '../gen/balance';

/**
 * The fresh-session rule (R2963). A registered account adds a sign-in
 * method, or deletes itself with no password, only on a session that a
 * sign-in opened a short time ago. The code arrives both ways: as an
 * `ApiError` from the register and the delete, and on the page address
 * from the start of a link. The ACCOUNT page answers it with SIGN IN
 * AGAIN (`ui/hub/panels/reauth.ts`).
 */
export { CODE_REAUTH_REQUIRED } from '../gen/balance';

/**
 * The two refusals of operator impersonation (R740, R741).
 *
 * `imp_code_expired` is one answer for four states of a mint code: it never
 * existed, it is spent, it is older than its life, or a newer mint replaced
 * it. The operator has nothing to fix and mints a second code.
 *
 * `impersonation_refused` is the guard on the four routes an operator must
 * not take on a player's behalf. No screen switches on it: the sentence the
 * server sends is what a person reads.
 */
export { CODE_IMP_CODE_EXPIRED, CODE_IMPERSONATION_REFUSED } from '../gen/balance';

/** One failed API call. `status` is 0 when the request never got an answer. */
export class ApiError extends Error {
  /**
   * The JSON path a theme refusal names, `body.tokens.accent` or a link's
   * category, and the closed reason token of an `invalid_theme` refusal
   * (themes.md §11.2, R813). Both are '' on every other refusal.
   */
  readonly field: string;
  readonly reason: string;
  /**
   * The `Retry-After` of the answer, in seconds, or 0 where it carried
   * none. A refusal of a bucket states its wait there; the REPORT sheet
   * reads it (fault-reports.md §4.1, R1621).
   */
  readonly retryAfterSec: number;

  constructor(
    readonly status: number, readonly code: string, message: string,
    detail: { field?: string; reason?: string; retryAfterSec?: number } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.field = detail.field ?? '';
    this.reason = detail.reason ?? '';
    this.retryAfterSec = detail.retryAfterSec ?? 0;
  }

  /**
   * True when this origin has no meta server behind it: the fetch itself
   * failed, or something that is not the meta API answered (a Vite dev
   * server hands back `index.html` for an unknown path). The caller must
   * treat this as "run the legacy direct mode", never as "the player is
   * signed out".
   */
  get unavailable(): boolean {
    return this.code === CODE_UNAVAILABLE;
  }
}

/** The message of a failure (R1048): it lives in the leaf `util/error.ts`,
 *  and every screen that prints a failed call reads it here. */
export { messageOf } from '../util/error';

/**
 * Turns one failed response into an ApiError. Pure, so the error shape is
 * unit-testable without a network.
 *
 * - The typed shape becomes the code and the message it carries.
 * - A body that is not JSON means the answer came from something that is
 *   not the meta API, so the error is `unavailable`.
 * - JSON in an unknown shape keeps the status but reports a stable code.
 */
export function parseError(status: number, body: string, retryAfter: string | null = null): ApiError {
  const wait = Number.parseInt(retryAfter ?? '', 10);
  const retryAfterSec = Number.isFinite(wait) && wait > 0 ? wait : 0;
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return new ApiError(status, CODE_UNAVAILABLE, S.no_meta_api({ status }));
  }
  if (typeof parsed === 'object' && parsed !== null) {
    const err = (parsed as { error?: unknown }).error;
    if (typeof err === 'object' && err !== null) {
      const e = err as Record<string, unknown>;
      const code = str(e.code);
      if (code !== '') {
        return new ApiError(status, code, str(e.message) || code,
          { field: str(e.field), reason: str(e.reason), retryAfterSec });
      }
    }
  }
  return new ApiError(status, CODE_UNEXPECTED, S.unexpected_answer({ status }));
}

/** The account, as `viewAccount` in `authroutes.go` writes it. */
export interface Account {
  id: string;
  name: string;
  kind: string;
  honourCore: number;
  honourEvent: number;
  /** The account's Clan Honour: what a Clan War pays, apart from the core
   *  total (R1512). Absent from a server older than that ruling. */
  honourClanwar?: number;
  createdAt: number;
  /**
   * Whether the one free name change is still unspent (R226).
   * The view greys the control from this flag rather than finding out by a
   * refusal. An account that spent it asks support.
   */
  canRename: boolean;
  /**
   * The banner this account fights under, 1 to 5, and 0 while no faction is
   * rolled yet (R240). The first join rolls one.
   *
   * It is optional here, so a server that predates the field does not break
   * this client: an absent value reads as 0, which the hub draws as no
   * faction at all.
   */
  faction?: number;
}

/** One entitlement grant the account holds. */
export interface Entitlement {
  code: string;
  bit: number;
  expiresAt?: number;
}

/**
 * What the last heartbeat said about one seat (R223): the
 * rank, the sov held, the build queue and what happened while the player
 * was out.
 *
 * Every second here is a **wall** second. The game server measures a queue
 * on its own world clock (numbers.md §19) and converts before it beats, the
 * same way `WorldInfo.seasonEndAt` carries a season end.
 */
export interface SeatSummary {
  /** 1 is the leader. */
  rank: number;
  /** What the rank is out of, the autopilot's empires included. */
  of: number;
  score: number;
  /** The sov this commander took that still flies its faction's colours. */
  cellsOwned: number;
  /** True while the build queue still holds work. */
  queueBusy: boolean;
  /** What the queue would still take, in wall seconds. */
  queueFreeSec: number;
  /** The queue ceiling, in wall seconds. */
  queueMaxSec: number;
  queueLen: number;
  /** The whole inbox badge. */
  unread: number;
  /** The part of `unread` that is an attack on this empire. */
  unreadBattle: number;
  /**
   * The return briefing of the absence so far (R1605,
   * ui-map-interface.md §12.6): absent while a client sits at the seat,
   * for a short absence and for an absence with nothing to say.
   */
  briefing?: SeatBriefing;
  /**
   * The next vulnerability window of the world and the sieges against the
   * seat's faction (R1743, ui-map-interface.md §16.6), on every beat.
   * Absent for a beat that carried none.
   */
  watch?: SeatWatch;
  /**
   * When the game server last described the seat, in unix seconds on the
   * wall clock. The view says "as of a minute ago" from it.
   */
  asOf: number;
}

/** The seat beat's return briefing as `/api/me` serves it (R1605,
 *  ui-map-interface.md §12.6). Every second is a WALL second, counted
 *  from the seat's `asOf`. */
export interface SeatBriefing {
  awaySec: number;
  sieges: SeatSiege[];
  cellsWon: number;
  cellsLost: number;
  fleetsSunk: number;
  fleetsLost: number;
  sickbay: { name: string; backSec: number }[];
  lotsSold: number;
  saleCredits: number;
  buildLevels: number;
  techNodes: number;
}

/** One strategic object of the seat's faction under an enemy pledge.
 *  `windowInSec` is WALL seconds from the seat's `asOf`, negative while
 *  the window is open. */
export interface SeatSiege {
  siteName: string;
  attacker: number;
  windowInSec: number;
}

/** The seat watch as `/api/me` serves it (R1743, ui-map-interface.md
 *  §16.6). Every second is a WALL second, counted from the seat's `asOf`.
 *  `windowSite` is empty for a map with no strategic object. */
export interface SeatWatch {
  windowSite: string;
  /** Until the next window opens; 0 or below while it is open. */
  windowInSec: number;
  /** Until that window shuts. */
  windowShutSec: number;
  sieges: SeatSiege[];
}

/** One seat the account holds in a world. */
export interface Placement {
  worldId: number;
  world: string;
  mode: string;
  region: string;
  season: number;
  status: string;
  since: number;
  /**
   * When the world's season ends, in unix seconds on the wall clock, or
   * absent when no heartbeat has said or the placement is of an earlier
   * season (R1743). The seat card counts SEASON ENDS down to it.
   */
  seasonEndAt?: number;
  /**
   * The seat's live state, when a fresh heartbeat described it. It is
   * absent when the world has not beaten for a minute, and when the newest
   * beat belongs to an earlier season (R223).
   */
  seat?: SeatSummary;
}

/** One shard, as `/api/worlds` and the join answer describe it. */
export interface WorldInfo {
  id: number;
  name: string;
  mode: string;
  region: string;
  season: number;
  status: string;
  population: number;
  freeSeats: number;
  maxSeats: number;
  /**
   * When this world's season ends, in unix seconds on the **wall clock**,
   * or absent when no heartbeat has said. The game server measures a season
   * on its own world clock (numbers.md §19) and converts before it beats,
   * so the caller subtracts and never has to know which game clock the
   * figure came from. Subtract it from `WorldList.now`, not from the
   * browser's clock.
   */
  seasonEndAt?: number;
}

/** The whole answer to `GET /api/worlds`. */
export interface WorldList {
  /**
   * The server's own instant, in unix seconds. Every countdown the hub
   * draws is measured against this and not against `Date.now()`, because a
   * telephone that has been asleep carries a clock that has drifted.
   */
  now: number;
  worlds: WorldInfo[];
}

/**
 * One scheduled run of one mode, as `/api/modes` describes it
 * (R220). Both instants are unix seconds on the wall clock,
 * and an absent one means "no bound on that side".
 *
 * Nothing here is a formatted string. The server ships the instants and the
 * hub renders "OPENS FRI 19:00 · IN 2D 06H" from them, because a
 * countdown ticks and a string does not.
 */
export interface ModeWindow {
  /** The name of this run, such as "Clan War III". */
  label?: string;
  opensAt?: number;
  closesAt?: number;
}

/** One mode tile on the hub, as `GET /api/modes` describes it. */
export interface ModeInfo {
  mode: string;
  /**
   * Whether the schedule lets a join through now. It says nothing about the
   * entitlement gate: an account that holds no key still sees the schedule,
   * because the schedule is what tells it whether the key is worth buying.
   */
  open: boolean;
  /** Whether this mode has any window at all. `core` has none. */
  scheduled: boolean;
  /** The window that holds `now`, when one does. */
  current?: ModeWindow;
  /** The earliest window that opens after `now`, when one exists. */
  next?: ModeWindow;
  /** The entitlement key a join needs, or absent when a join needs none. */
  entitlement?: string;
  /**
   * The cheapest tier that sells that key, or absent where no tier sells it
   * (R232). The tile names what a player can buy, and a key's own code is
   * not a thing anybody can buy. The tier table is operator configuration,
   * so the client cannot work this out and the server ships it. An absent
   * tier means the key is an admin grant, and the tile must then point at
   * no shop.
   */
  tier?: string;
  /** Open worlds of this mode with a free seat. */
  worlds: number;
  /** Human seats taken across those worlds. */
  population: number;
}

/**
 * What the arcade coordinators report right now (arcade.md §11). The meta
 * server asks every coordinator it is configured for, and it caches the
 * answer for about 12 s, so these figures are seconds old and not live.
 *
 * The whole block is optional, because a deployment can configure no
 * coordinator at all. Absent means "this server serves no arcade fleet",
 * and a reader must then fall back on the world registry rather than draw
 * a zero. A server that predates the block also sends nothing, so the same
 * fallback covers both.
 */
export interface ArcadeLive {
  /** Matches fighting right now, across the whole arcade fleet. */
  running: number;
  /** Human seats in those running matches. Bots do not count. */
  humans: number;
  /** Map categories the arcade fleet serves. */
  categories: number;
  /** Arcade processes the meta server is configured for. */
  coordinators: number;
  /** How many of those coordinators answered. */
  healthy: number;
  /**
   * Whether a join can work now: at least one coordinator answered, is not
   * draining, and serves a category. It is authoritative — a reader must
   * not work the gate out again from the figures above.
   */
  open: boolean;
  /** How old the figures are, in seconds. */
  ageSec: number;
}

/** The maintenance window ahead, as `GET /api/modes` states it (the
 *  release manager, deployment.md §6.1). Absent where none is planned. */
export interface MaintenanceWindow {
  /** The unix second the gate goes up. */
  startsAt: number;
  /** The unix second the gate is planned to come down. */
  endsAt: number;
  /** The operator's line for the player, or the empty string. */
  note: string;
}

/** The whole answer to `GET /api/modes`. */
export interface ModeList {
  /** The server's own instant, in unix seconds. Countdowns start here. */
  now: number;
  modes: ModeInfo[];
  /** The arcade fleet's live figures, or absent where the deployment
   *  configures no coordinator. */
  arcade?: ArcadeLive;
  /** The next maintenance window, or absent where none is planned
   *  (deployment.md §6.1). The PLAY panel counts down to it. */
  maintenance?: MaintenanceWindow;
}

/**
 * The daily arcade run counter (R221), as `arcade.go` writes it. A run is
 * one arcade seat, and the cap comes from the account's tier: free 1 a day,
 * supporter 3 a day, premium unlimited.
 *
 * `unlimited` is a state of its own, so the client never recognises a large
 * number: when it is true, `limit` and `remaining` are absent because there
 * is no number to draw.
 */
export interface ArcadeRuns {
  /** Runs consumed in the window that holds now. */
  used: number;
  /** True when the account's tier carries no cap. */
  unlimited: boolean;
  /** Runs a day. Absent when `unlimited` is true. */
  limit?: number;
  /** Runs still left today. Absent when `unlimited` is true. */
  remaining?: number;
  /**
   * When the counter goes back to zero, in unix seconds. It is an absolute
   * instant, and the countdown is the client's to draw. It is wall time:
   * the world clock and the map clock pace a world, and the meta server
   * runs none.
   */
  resetsAt: number;
}

/** The answer to `GET /api/me`. */
export interface Me {
  account: Account;
  entitlements: Entitlement[];
  placements: Placement[];
  arcade: ArcadeRuns;
  /** The clan the account is in, or null where it is in none (R232). The
   *  clanwar mode demands a clan, so the hub tile says why it cannot be
   *  pressed without a second request. It is optional here, so a server
   *  that predates the field does not break this client. */
  clan?: Clan | null;
  /**
   * The sign-in methods this account holds, sorted (R621): `email`,
   * `google` and `apple`. An account may hold all three, and one identity
   * of each.
   *
   * It is optional here, so a server that predates the field does not break
   * this client. An absent field reads as an empty list, and the SETTINGS
   * panel then offers every method as a link.
   */
  identities?: string[];
  /**
   * The operator who plays this account, as `ops:<user>`, while an
   * impersonated session is open (deployment.md, the impersonation
   * section). An ordinary session
   * carries no such field, and that absence is the whole test the client
   * makes: a value here raises the gold strip of `ui/impbar.ts`.
   *
   * It is optional here, so a server that predates the field does not break
   * this client.
   */
  impersonatedBy?: string;
  /**
   * The feature flags the environment plays (v2-the-deep.md §17.2, R2656):
   * the flags of the meta config and the flags an open world latched. The
   * theme workshop and the ship symbols page hide a row whose flag is off
   * (`ui/fieldflags.ts`), and the hub ALERTS card shows a flagged switch
   * while the set holds its flag (`envFlagRead`).
   *
   * It is optional here, so a server that predates the field does not break
   * this client. An absent field hides no row of the two pages, and the
   * ALERTS card of the hub then shows no flagged switch.
   */
  features?: string[];
  /**
   * The environment tag of the meta config (R2872), such as `DEV/BETA`. The
   * hub header shows it in brackets between the logo and the player name
   * (ui-map-interface.md §16.3). It is absent where the environment states
   * no tag, and the header then shows none.
   */
  hubTag?: string;
  /**
   * The Core seasons of this account, the running one included (R1305,
   * R1566): an account that has not joined the running season counts it
   * as well.
   *
   * It is optional here, so a server that predates the field does not break
   * this client.
   */
  coreSeasons?: number;
  /**
   * The new-commander gate (player-reach.md §4.7, R1566): the account
   * plays its first `ADVISOR_DEFAULT_ON_SEASONS` Core seasons. A later
   * surface for new commanders reads this and no rule of its own.
   */
  newCommander?: boolean;
  /**
   * The advisor switch the account stores (R1566): `auto` until the
   * player or the gate chose, then `on` or `off`. `putAdvisor` writes it.
   */
  advisor?: AdvisorWord;
  /** The switch the next join states to the world: the player's word, or
   *  for `auto` the gate (R1566). */
  advisorOn?: boolean;
  /**
   * The notice cards of a moderation decision (deployment.md §7.6, R3125):
   * a statement of reasons or the decision on a report that reached no mail
   * address. The hub shows each one until the player dismisses it.
   *
   * It is optional here, so a server that predates the field does not break
   * this client.
   */
  notices?: AccountNotice[];
}

/** One notice card of a moderation decision (R3125). The server writes the
 *  words in the stored language of the account: `body` is plain text, one
 *  paragraph to each block that a blank line ends. */
export interface AccountNotice {
  id: number;
  kind: 'reasons' | 'decision';
  subject: string;
  body: string;
  at: number;
}

/** The three words of the account's advisor switch (R1566). */
export type AdvisorWord = 'auto' | 'on' | 'off';

/** The answer to `POST /api/join`: where to dial, and what to say. */
export interface Join {
  /** Already carries `?world=<id>` — the meta server puts it there. */
  wsUrl: string;
  /** Opaque to the client. It goes on the websocket URL as `?ticket=`. */
  ticket: string;
  world: WorldInfo;
  newPlacement: boolean;
  expiresIn: number;
  /**
   * Arcade only (arcade.md §2.3): the daily runs this account had left
   * when the ticket was minted. `-1` means the tier carries no cap, and
   * the field is absent for every other mode. Minting spends nothing —
   * the run goes at match lock — so this is the count *before* the match.
   * A spent account never reaches here: `POST /api/join` refuses it with
   * HTTP 429 and code `rate_limited`, unless it asked for a drill.
   */
  runsLeft?: number;
  /**
   * Arcade only (arcade.md §17.5, R1564): true when the account has no run
   * left today and asked for a drill. The ticket then opens a drill and
   * nothing else, and the coordinator refuses every queue and hosting.
   */
  drillOnly?: boolean;
}

/** What the caller may ask matchmaking for. */
export interface JoinRequest {
  mode?: string;
  region?: string;
  /** A shard the player picked. Omit it to let matchmaking decide. */
  world?: number;
  /** What an arcade join is for: omitted for a match, `drill` for a
   *  drill, which a spent account may still start (arcade.md §17.5), or
   *  `cup` for the cup game of a cup team, which takes no run either
   *  (clan-cup.md §5). */
  purpose?: 'drill' | 'cup';
}

/** The refusal of a request that got no answer: the last attempt waited
 *  too long (R1203), or nothing answered at all — a refused connection, a
 *  DNS failure, a blocked request — so this origin has no meta server. */
function transportError(err: unknown): ApiError {
  if (isTimeoutError(err)) return new ApiError(0, CODE_TIMEOUT, S.meta_timeout);
  return new ApiError(0, CODE_UNAVAILABLE, S.meta_no_answer({ error: String(err) }));
}

/** `path` with the set parameters behind a `?`, in their order, or `path`
 *  alone when none is set. An empty string, a 0 and an absent value are
 *  not set, so a caller passes its optional fields as they stand. */
function withQuery(path: string, params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [key, v] of Object.entries(params)) if (v) q.set(key, String(v));
  const query = q.toString();
  return query ? `${path}?${query}` : path;
}

/** The device's own unix second: the fallback where an answer names no
 *  `now` of the server's. */
function nowSec(): number {
  return Math.floor(Date.now() / 1000);
}

/**
 * The header that states the page language to the meta server (i18n.md
 * §1.2, R2207). The server reads it and never `Accept-Language`, so the
 * language of the browser never decides. The meta API is same-origin, so
 * the header costs no preflight.
 */
export const LANG_HEADER = 'X-Ap-Lang';

/**
 * One request. Same-origin, cookie-carrying, JSON in and JSON out.
 *
 * It goes through `net/request.ts` (R1706): a GET makes a short first
 * attempt and more attempts after it, because a dead HTTP/3 connection
 * holds a request with no answer, and every other method makes one
 * attempt of `API_TIMEOUT_MS`. The read of the body is part of the
 * attempt, so a server that sends the head and then stalls must not hold
 * the page either (R1203). `idempotent` marks a POST that a second send
 * cannot harm.
 */
async function call<T>(method: string, path: string, body?: unknown,
  opts: { idempotent?: boolean; notModified?: boolean } = {}): Promise<T> {
  let res: Response;
  let text: string;
  try {
    ({ res, text } = await requestText(path, {
      method,
      credentials: 'include',
      // The language this page reads (i18n.md §1.2, R857, R2207). Every
      // refusal the meta server writes, and every mail it raises from
      // this request, answers in it. It rides a header of our own and
      // not `Accept-Language`, which the browser fills with its own
      // language. A fetch may set the header; a WebSocket may not, which
      // is why the game server reads a query instead.
      headers: {
        [LANG_HEADER]: LOCALE,
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    }, opts));
  } catch (err) {
    throw transportError(err);
  }
  // A poll that names the revision it holds takes 304 as "unchanged"
  // (`net/customlobby.ts`, R3088): no body, and null to the caller.
  if (opts.notModified && res.status === 304) return null as T;
  if (!res.ok) throw parseError(res.status, text, res.headers?.get('Retry-After') ?? null);
  try {
    return JSON.parse(text) as T;
  } catch {
    // A 200 that is not JSON is a dev server answering with index.html.
    throw new ApiError(res.status, CODE_UNAVAILABLE, S.not_json);
  }
}

/**
 * One request of a route module beside this one (`net/customlobby.ts`):
 * the same transport, header and error shape as every call here. A GET
 * with `notModified` answers null on a 304.
 */
export function apiCall<T>(method: string, path: string, body?: unknown,
  opts: { idempotent?: boolean; notModified?: boolean } = {}): Promise<T> {
  return call<T>(method, path, body, opts);
}

/**
 * The account this browser is signed in as. 401 means "show the landing".
 *
 * Every answer refreshes the entitlement set the look resolver reads
 * (themes.md §8, R809), so each surface that reads the account also keeps
 * the set current, and the game entry resolves a look with no second call.
 * A 401 stores the empty set: a signed-out browser holds no key.
 */
export async function getMe(): Promise<Me> {
  try {
    const me = await call<Me>('GET', '/api/me');
    rememberEntitlements((me.entitlements ?? []).map((e) => e.code));
    return me;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) rememberEntitlements([]);
    throw err;
  }
}

/**
 * The public world list with the server's instant beside it. It needs no
 * session. A caller that draws a season countdown subtracts from `now`.
 */
export async function getWorldList(mode?: string, region?: string): Promise<WorldList> {
  const out = await call<WorldList>('GET', withQuery('/api/worlds', { mode, region }));
  return { now: out.now ?? nowSec(), worlds: out.worlds ?? [] };
}

/**
 * The mode schedule the hub draws its countdowns from. It needs no session:
 * the tiles are public, and a player who is not signed in must see when a
 * mode opens (R220).
 */
export async function getModes(): Promise<ModeList> {
  const out = await call<ModeList>('GET', '/api/modes');
  return {
    now: out.now ?? nowSec(),
    modes: out.modes ?? [],
    // Carried through as it came. An absent block stays absent, because
    // absent is the signal that this deployment configures no coordinator.
    arcade: out.arcade,
    // The same rule: absent is the signal that no window is planned.
    maintenance: out.maintenance,
  };
}

/** "Jump right in": a guest account and a session, in one call. The
 *  account signal of the guest creation follows it (R1310). */
export async function authGuest(name?: string): Promise<{ account: Account }> {
  const out = await call<{ account: Account }>('POST', '/api/auth/guest', { name: name ?? '' });
  void reportSignal('guest');
  return out;
}

/**
 * Creates an email identity. With a guest session in the cookie the meta
 * server upgrades that account in place, so the guest keeps its captains
 * and its Honour. The caller reads `/api/me` again afterwards.
 */
export async function authRegister(email: string, password: string, name?: string): Promise<{ account: Account }> {
  const out = await call<{ account: Account }>('POST', '/api/auth/register',
    { email, password, name: name ?? '' });
  void reportSignal('signin');
  return out;
}

/**
 * The sign-in methods this deployment offers (R621). It needs no session:
 * the landing screen calls it before anybody is signed in.
 *
 * `email` is always in the answer, because the register route needs no
 * configuration. A provider is in it only while its configuration block is
 * there, so a screen draws a provider button from this list and never from
 * a build-time constant.
 */
export async function getSignInMethods(): Promise<string[]> {
  const out = await call<{ methods?: string[] }>('GET', '/api/auth/methods');
  return out.methods ?? [];
}

/**
 * The deep link a sign-in with a provider carries through the provider
 * (R1659): the hub section and the page inside it that the page address
 * named at the arrival, for example `clan` and `cup`.
 */
export interface SignInLink {
  tab?: string;
  page?: string;
  /** The page of the SETTINGS section (`SECTION_PARAM`, R1731). */
  section?: string;
}

/**
 * Where the browser goes to sign in with one provider (R621).
 *
 * It is a top-level navigation and never a fetch: the route sets the state
 * cookie and redirects to the provider, and the provider then comes back to
 * this origin. A caller assigns this to `location` and expects the page to
 * go.
 *
 * `link` is the deep link the landing screen carries (R1659). The route
 * keeps it in the state cookie, and the callback writes it back into the
 * address it answers, so the hub opens the section and the page the
 * address named. The query words are those of the hub address: `tab` of
 * `readSignIn`, and `PAGE_PARAM` and `SECTION_PARAM` of `ui/hub/panel.ts`,
 * which this module cannot import, because that module imports this one.
 */
export function oauthStartPath(provider: string, link?: SignInLink): string {
  // A sign-in is a navigation and sets no header, so the page language
  // rides the query; the meta server keeps it through the provider's
  // round trip for the welcome mail (i18n.md §1.2, R2207).
  return withQuery(`/api/auth/oauth/${provider}/start`,
    { tab: link?.tab, page: link?.page, section: link?.section, lang: LOCALE });
}

/**
 * A one-use start ticket for the session of this page (mobile-apps.md §6,
 * R2938). The sign-in sheet of the iOS app shell holds the cookie jar of
 * Safari, so the start route cannot read the session of the page. The
 * ticket on the start path names the account of the page in its place.
 */
export async function oauthStartTicket(): Promise<string> {
  const out = await call<{ ticket?: string }>('POST', '/api/auth/oauth/ticket');
  if (!out.ticket) throw new Error('the meta server answered no start ticket');
  return out.ticket;
}

/** Opens a session from an email address and a password. The account
 *  signal of the sign-in follows it (R1310). */
export async function authLogin(email: string, password: string): Promise<{ account: Account }> {
  const out = await call<{ account: Account }>('POST', '/api/auth/login', { email, password });
  void reportSignal('signin');
  return out;
}

/**
 * Asks for a reset link (R624). It needs no session.
 *
 * The answer is 200 for every address the shape check accepts, whether an
 * account holds it or not, so a caller must never read a success as "that
 * address is known here". The refusals that do arrive are typed and carry
 * the server's own sentence: `mail_off` on a deployment with no mail
 * sender, `rate_limited` on a second ask too soon, `invalid_email` on an
 * address that is not one.
 */
export async function forgotPassword(email: string): Promise<void> {
  await call<{ ok: boolean }>('POST', '/api/auth/password/forgot', { email });
}

/**
 * Replaces the password of the account this browser is signed in as
 * (R626). It needs a session.
 *
 * The browser that asks keeps its session; every OTHER session of the
 * account is revoked, which is what a person means by "change my
 * password". The refusals are typed: `invalid_credentials` when the
 * current password is wrong, `weak_password` when the new one is refused,
 * and `conflict` when the account holds no email identity at all — the
 * ACCOUNT card offers the form only when `identities` names `email`, so
 * the last one answers a call that went around the card.
 */
export async function changePassword(current: string, next: string): Promise<void> {
  await call<{ ok: boolean }>('POST', '/api/me/password', { current, next });
}

/**
 * Deletes the account this browser is signed in as (deployment.md §10.4).
 *
 * `name` is the account name typed as it stands, and `password` the
 * password of the email method; an account with no password sends ''.
 * The answer clears the session cookie. Refusals: `delete_confirm` for a
 * name that is not the account's, `invalid_login` for a wrong password,
 * `subscription_active` while a paid plan still runs, and
 * `impersonation_refused` on an operator's session.
 */
export function deleteMe(name: string, password: string): Promise<{ deleted: boolean }> {
  return call<{ deleted: boolean }>('DELETE', '/api/me',
    password === '' ? { name } : { name, password });
}

/** Removes one notice card of the account for good (R3125). */
export function dismissNotice(id: number): Promise<{ dismissed: boolean }> {
  return call<{ dismissed: boolean }>('DELETE', `/api/notices/${id}`);
}

/**
 * Spends the one-time code an operator minted, and opens an impersonated
 * session on the account the code names (deployment.md, the impersonation
 * section).
 *
 * The answer sets the ordinary session cookie, so every later call of this
 * module speaks as the player. The caller reads `/api/me` afterwards.
 *
 * The code is single-use and short-lived. Every refusal — a code that
 * never existed, a code another window already spent, and a code that is
 * too old — arrives as one error, so the browser never learns which of the
 * three it holds.
 */
export function impersonate(code: string): Promise<{ account: Account }> {
  return call<{ account: Account }>('POST', '/api/impersonate', { code });
}

/**
 * Ends the impersonated session and clears the cookie.
 *
 * It refuses a session that no operator opened, so a player cannot end a
 * real session through it. The caller reloads the page afterwards: the
 * client holds the account in a screen and in the state cache, and a
 * reload is the one way that both let go of it.
 */
export function stopImpersonating(): Promise<{ ok: boolean }> {
  return call<{ ok: boolean }>('POST', '/api/impersonate/stop');
}

/**
 * Changes the player name (R226). A player gets ONE free
 * change: a second call answers 409 with `rename_spent`, and the player
 * asks support. A name another account holds answers 409 with `name_taken`,
 * and a name the rule refuses answers 400 with `invalid_name`.
 *
 * A world reads a name only from the join ticket, so a rename lands in a
 * world at the next join. The client sends the resume token and the ticket
 * on one dial, and the game server now reads the ticket's name on a resume
 * as well (R249), so the next PLAY press carries the new name whether the
 * browser holds a token for that world or not. A session that is already at
 * sea in another tab keeps the old name until it comes back.
 */
export function renameAccount(name: string): Promise<{ account: Account }> {
  return call<{ account: Account }>('POST', '/api/me/name', { name });
}

/**
 * The account's own settings blob (R256).
 *
 * The blob belongs to the account, so it follows the player to any browser
 * and any device. **The meta server stores it and reads no field of it**:
 * `ui/settings.ts` owns the schema, and this call carries whatever that
 * module wrote. `settings` is therefore an opaque object here as well.
 *
 * `updatedAt` is a wall-clock unix second, and it is 0 for an account that
 * has stored nothing. That is not a failure: it is the state every new
 * account is in, and the answer is a 200 with an empty object.
 */
export interface StoredSettings {
  settings: Record<string, unknown>;
  updatedAt: number;
  /**
   * The standing-orders switch (R1256, autopilot.md §14). It is a typed
   * field of the account and NOT a key of the blob, because the server
   * acts on it; `putStandingOrders` writes it. A server that predates the
   * field sends none.
   */
  standingOrders?: boolean;
  /**
   * The stated hours of the squad finder (R1304, player-reach.md §5.3). A
   * typed field of the account like the switch above; `putStatedHours`
   * writes it. A server that predates the field sends none.
   */
  statedHours?: StatedHours;
}

/** The four bands of the stated hours, and '' for no statement. */
export const HOURS_BANDS = ['', 'morning', 'afternoon', 'evening', 'night'] as const;
export type HoursBand = (typeof HOURS_BANDS)[number];

/** The stated hours: the local band and the IANA time zone it is local to. */
export interface StatedHours {
  hours: HoursBand;
  timeZone: string;
}

/** Reads the account's stored settings. It needs a session. */
export async function getSettings(): Promise<StoredSettings> {
  const out = await call<StoredSettings>('GET', '/api/settings');
  return {
    settings: out.settings ?? {}, updatedAt: out.updatedAt ?? 0,
    standingOrders: out.standingOrders, statedHours: out.statedHours,
  };
}

/**
 * Replaces the account's stored settings whole. It needs a session.
 *
 * There is no merge and no partial write: the client sends the settings it
 * holds, and the last write wins. A merge would resurrect a switch the
 * player had just turned off on another device. The caller must debounce,
 * because a volume drag writes on every step and each PUT is a whole blob.
 */
export function putSettings(settings: Record<string, unknown>): Promise<StoredSettings> {
  return call<StoredSettings>('PUT', '/api/settings', settings);
}

/**
 * Sets the standing-orders switch (R1256). It needs a session. The write
 * lands at once and is never debounced: the Core world reads the switch
 * on its next heartbeat.
 */
export function putStandingOrders(on: boolean): Promise<{ standingOrders: boolean }> {
  return call<{ standingOrders: boolean }>('PUT', '/api/settings/standing-orders', { on });
}

/**
 * Sets the advisor switch of the account (R1566). It needs a session. The
 * write lands at once and is never debounced: the next join states it to
 * the world. The server takes `on` and `off` alone.
 */
export function putAdvisor(on: boolean): Promise<{ advisor: AdvisorWord; advisorOn: boolean }> {
  return call<{ advisor: AdvisorWord; advisorOn: boolean }>('PUT', '/api/settings/advisor',
    { advisor: on ? 'on' : 'off' });
}

/**
 * Sets the stated hours (R1304). It needs a session. The write lands at
 * once: the next season manifest reads it. A band of '' clears the
 * statement, and the server then stores no zone.
 */
export function putStatedHours(hours: HoursBand, timeZone: string): Promise<{ statedHours: StatedHours }> {
  return call<{ statedHours: StatedHours }>('PUT', '/api/settings/stated-hours', { hours, timeZone });
}

// --- push notifications (player-reach.md §3.4, §3.5, R1300, R1301) --------

/**
 * The code the meta server answers `GET /api/push/key` and `PUT /api/push`
 * with when it has no VAPID key, generated from `data/contracts/api.toml`
 * (R1300, R1303).
 */
export { CODE_PUSH_OFF } from '../gen/balance';

/** The preferences of one subscription, as the meta server stores them. */
export interface PushPrefs {
  /** The classes that are on, in the order of `PUSH_CLASSES`. */
  classes: string[];
  /** The start of the quiet hours, `HH:MM` in local time. */
  quietStart: string;
  /** The end of the quiet hours, `HH:MM` in local time. */
  quietEnd: string;
  /** The IANA time zone of the browser. */
  timeZone: string;
  /** Whether the siege class sends in the quiet hours. */
  siegeAtNight: boolean;
}

/** What `GET /api/push` answers for one endpoint. */
export type PushState = ({ subscribed: true } & PushPrefs) | { subscribed: false };

/** The body of `PUT /api/push`: the subscription of the browser and the
 *  preferences. */
export interface PushSubscriptionBody extends PushPrefs {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

/** The public VAPID key, base64url. It needs no session. */
export async function getPushKey(): Promise<string> {
  const out = await call<{ publicKey?: string }>('GET', '/api/push/key');
  const key = str(out.publicKey);
  if (key === '') throw new ApiError(200, CODE_UNEXPECTED, S.unexpected_answer({ status: 200 }));
  return key;
}

/** Reads the stored subscription of one endpoint. It needs a session. */
export async function getPush(endpoint: string): Promise<PushState> {
  return pushStateOf(await call<Partial<PushState & PushPrefs>>(
    'GET', `/api/push?endpoint=${encodeURIComponent(endpoint)}`));
}

/** The answer of `GET /api/push`, field by field. */
function pushStateOf(out: Partial<PushState & PushPrefs>): PushState {
  if (out.subscribed !== true) return { subscribed: false };
  return {
    subscribed: true,
    classes: strList(out.classes),
    quietStart: str(out.quietStart),
    quietEnd: str(out.quietEnd),
    timeZone: str(out.timeZone),
    siegeAtNight: out.siegeAtNight === true,
  };
}

/** Stores or changes one subscription. It needs a session. */
export function putPush(body: PushSubscriptionBody): Promise<{ ok: boolean }> {
  return call<{ ok: boolean }>('PUT', '/api/push', body);
}

/** Removes one subscription. It needs a session. */
export function deletePush(endpoint: string): Promise<{ ok: boolean }> {
  return call<{ ok: boolean }>('DELETE', '/api/push', { endpoint });
}

/** The `kind` of an APNs address: the iOS store app (mobile-apps.md §7,
 *  R2927). A body without `kind` is a web push subscription. */
export const PUSH_KIND_APNS = 'apns';

/** The address of one install of the iOS store app: the APNs token, and
 *  the APNs environment of the build, `sandbox` or `production`. */
export interface ApnsAddress {
  token: string;
  env: string;
}

/** The body of `PUT /api/push` for the iOS store app: the address and the
 *  preferences. */
export interface ApnsSubscriptionBody extends PushPrefs, ApnsAddress {
  kind: typeof PUSH_KIND_APNS;
}

/** Reads the stored subscription of one APNs token. It needs a session. */
export async function getApnsPush(token: string): Promise<PushState> {
  return pushStateOf(await call<Partial<PushState & PushPrefs>>(
    'GET', withQuery('/api/push', { kind: PUSH_KIND_APNS, token })));
}

/** Stores or changes the subscription of one APNs token. */
export function putApnsPush(body: ApnsSubscriptionBody): Promise<{ ok: boolean }> {
  return call<{ ok: boolean }>('PUT', '/api/push', body);
}

/** Removes the subscription of one APNs token. */
export function deleteApnsPush(token: string): Promise<{ ok: boolean }> {
  return call<{ ok: boolean }>('DELETE', '/api/push', { kind: PUSH_KIND_APNS, token });
}

/** Ends the session and clears the cookie. */
export function authLogout(): Promise<{ ok: boolean }> {
  return call<{ ok: boolean }>('POST', '/api/auth/logout');
}

/** Matchmaking. The answer is the ws URL and the ticket to dial it with.
 *  The account signal of the world join follows it (R1310). */
export async function join(req: JoinRequest = {}): Promise<Join> {
  const out = await call<Join>('POST', '/api/join', {
    mode: req.mode ?? 'core',
    region: req.region ?? '',
    world: req.world ?? 0,
    // Only a drill states a purpose; every other join sends the body it
    // always sent.
    ...(req.purpose ? { purpose: req.purpose } : {}),
  });
  void reportSignal('join');
  return out;
}

// --- clan management (R228) -----------------------------------
//
// The clan lives on the meta server, because it outlives the world its
// squads play in. Every route below needs the session cookie, and every
// rule behind them is the server's: this module carries no role check of
// its own, and a hub that hides a control still gets the refusal if it
// calls anyway.
//
// The hub itself is a sibling task's work. Nothing here draws anything.
//
// A clan flies a faction and hands it to every member it takes in (R240).
// The founder picks it and nothing changes it after that. The squads come
// with the clan, so no control makes or removes one, and a squad carries no
// name: it is a number, 1 to `SQUAD_COUNT`. The server puts every new member in a
// squad at once, so the only squad write is the move.

/**
 * The clan codes (R341, generated).
 *
 * `no_clan` is what `GET /api/clan` answers when the account is in no
 * clan. It is a 404 and not a failure: it is the state most accounts are
 * in, and it is what draws the "find a clan" screen.
 *
 * `clan_closed` is a clan whose join policy is `closed` (R240). It is not
 * `clan_full`: the clan may have room and still take nobody, and the hub
 * must say which of the two it met. `clan_full` is a clan with no room
 * left.
 */
export { CODE_NO_CLAN, CODE_CLAN_CLOSED, CODE_CLAN_FULL } from '../gen/balance';

/** The three roles, highest first. A role is a rank: the hub compares two
 * of them rather than switching on a name. */
export const CLAN_ROLES = ['owner', 'officer', 'member'] as const;
export type ClanRole = (typeof CLAN_ROLES)[number];

/**
 * How a clan answers an application (R240).
 *
 *  - `open`   takes the applicant in at once, with no officer in the loop.
 *  - `invite` writes an application an officer answers. It is the default.
 *  - `closed` refuses every application with `clan_closed`.
 */
export const JOIN_POLICIES = ['open', 'invite', 'closed'] as const;
export type JoinPolicy = (typeof JOIN_POLICIES)[number];

/** The squads every clan has. The plan makes none and removes none.
 *  Generated from meta.toml [meta.social] — the servers hold the same
 *  figure. */
export { SQUAD_COUNT } from '../gen/balance';

/** One clan's own row, as a browse result and as the head of `GET /api/clan`. */
export interface Clan {
  id: number;
  name: string;
  tag: string;
  /** The banner the clan flies, 1 to 5. It is fixed at creation, and every
   *  member the clan takes in carries it (R240). It is optional so that a
   *  server which predates the field does not break this client. */
  faction?: number;
  /** `open`, `invite` or `closed`. An absent value reads as `invite`. */
  joinPolicy?: JoinPolicy;
  members: number;
  maxMembers: number;
  createdAt: number;
  /** The clan's Clan Honour: the sum of its clan ledger rows, one per
   *  Clan War world and season it sailed (R1512). Absent from a server
   *  older than that ruling. */
  honourClanwar?: number;
}

/** One person on a clan's roster. A member of a clan always holds a squad,
 * 1 to 5: the server puts a new member in one at the moment they join. A `0`
 * is only what a server older than this build answers. */
export interface ClanMember {
  /** The account's public id. Every write route names a member by it. */
  id: string;
  name: string;
  role: ClanRole;
  squad: number;
  joinedAt: number;
}

/** One open application to join. */
export interface ClanApplication {
  id: number;
  /** The applicant's public id. */
  account: string;
  name: string;
  note?: string;
  createdAt: number;
}

/**
 * Where the assignment batch seated one squad for the coming season. The
 * batch runs at the season roll, so a squad carries no seat until it has
 * run.
 */
export interface ClanSquadSeat {
  /** The season the seat belongs to. */
  season: number;
  /** The world the squad plays, by id and by name. */
  worldId: number;
  world: string;
  /** The region the world runs in, such as `eu`. */
  region: string;
  /** How many of the squad's members hold this seat. It is lower than the
   *  squad's member count when somebody joined after the batch ran. */
  members: number;
}

/**
 * One squad of the plan. The plan applies at the **next season start**: the
 * world a member already plays keeps the squad it placed them in, and
 * nothing here moves a commander who is at sea (R228).
 */
export interface ClanSquad {
  idx: number;
  members: number;
  maxMembers: number;
  /** The sum of the squad members' Honour in the core mode. The assignment
   *  batch ranks groups by this figure, so it is the squad's standing. It is
   *  optional so that a server which predates the field does not break this
   *  client. */
  honour?: number;
  /** The seat the batch gave this squad, or absent while the batch has
   *  seated nobody in it. It is optional for the same reason. */
  seat?: ClanSquadSeat;
}

/** The whole answer to `GET /api/clan`. */
export interface MyClan {
  clan: Clan;
  /** The caller's own role, which is what the hub draws its controls from. */
  role: ClanRole;
  members: ClanMember[];
  squads: ClanSquad[];
  /** Open applications. Only an officer or the owner may answer one. */
  applications: ClanApplication[];
}

/** The caller's own clan. A 401 means "sign in"; a 404 with `no_clan`
 * means "this account is in no clan", which is not a failure. */
export function getClan(): Promise<MyClan> {
  return call<MyClan>('GET', '/api/clan');
}

/** Clans by name or tag. An empty query lists the first page of all. */
export async function browseClans(q = ''): Promise<Clan[]> {
  const query = q.trim() === '' ? '' : `?q=${encodeURIComponent(q.trim())}`;
  const out = await call<{ clans?: Clan[] }>('GET', `/api/clans${query}`);
  return out.clans ?? [];
}

/** The hours match of FIND A CLAN (R1908, player-reach.md §5.5). */
export interface ClanHoursMatch {
  /** The reader's stated band as the server holds it, or '' for none. A
   *  band this build does not know stays the server's word. */
  hours: string;
  /** The clans whose members state the reader's hours, in rank order. */
  clans: Clan[];
  /** How many clans match before the list stops. */
  total: number;
}

/** The clans open to an application whose members state the reader's
 *  hours (R1908). It needs a session. A reader with no hours gets no clan
 *  and `hours` ''. */
export async function matchClanHours(): Promise<ClanHoursMatch> {
  const out = await call<Partial<ClanHoursMatch>>('GET', '/api/clans/hours');
  const clans = out.clans ?? [];
  return { hours: out.hours ?? '', clans, total: out.total ?? clans.length };
}

/**
 * Founds a clan with this account as its owner. Free for now: the design
 * charges credits, and credits are in the game where this listener cannot
 * reach them (R228).
 *
 * The faction is required (R240): a clan flies one banner, the founder
 * takes it too, and nothing changes it afterwards. The server refuses a
 * faction outside 1 to 5.
 */
export async function createClan(
  name: string, tag: string, faction: number, joinPolicy: JoinPolicy = 'invite',
): Promise<Clan> {
  const out = await call<{ clan: Clan }>(
    'POST', '/api/clans', { name, tag, faction, joinPolicy });
  return out.clan;
}

/**
 * The answer to an application (R240).
 *
 * An `open` clan takes the applicant in inside the call, so it answers
 * `joined` with no application at all: there is no officer to wait for and
 * no row to show. Every other clan answers the application it wrote.
 */
export interface ApplyResult {
  joined: boolean;
  application?: ClanApplication;
}

/** Asks to join one clan. The note is a short word to its officers. */
export async function applyToClan(clanId: number, note = ''): Promise<ApplyResult> {
  const out = await call<{ joined?: boolean; application?: ClanApplication }>(
    'POST', `/api/clans/${clanId}/apply`, { note });
  return { joined: out.joined === true, application: out.application };
}

/** Takes one applicant in. Officer or owner only. */
export function approveApplication(id: number): Promise<{ ok: boolean }> {
  return call<{ ok: boolean }>('POST', `/api/clan/applications/${id}/approve`);
}

/** Turns one applicant away. Officer or owner only. */
export function refuseApplication(id: number): Promise<{ ok: boolean }> {
  return call<{ ok: boolean }>('POST', `/api/clan/applications/${id}/refuse`);
}

/** Leaves the clan. The owner hands it over first, or leaves as the last
 * member and takes the clan with them. */
export function leaveClan(): Promise<{ ok: boolean }> {
  return call<{ ok: boolean }>('POST', '/api/clan/leave');
}

/** Removes one member. The caller must outrank them. */
export function removeMember(publicId: string): Promise<{ ok: boolean }> {
  return call<{ ok: boolean }>('DELETE', `/api/clan/members/${encodeURIComponent(publicId)}`);
}

/** Grants or revokes a role. The owner only. Setting a member to `owner`
 * hands the clan over: the old owner becomes an officer. */
export function setRole(publicId: string, role: ClanRole): Promise<{ ok: boolean }> {
  return call<{ ok: boolean }>(
    'POST', `/api/clan/members/${encodeURIComponent(publicId)}/role`, { role });
}

/** Sets how the clan answers an application. Officer or owner only. */
export function setJoinPolicy(policy: JoinPolicy): Promise<{ ok: boolean }> {
  return call<{ ok: boolean }>('POST', '/api/clan/policy', { joinPolicy: policy });
}

/** Moves one member into squad `idx`. A member always holds one squad, so
 * this replaces whatever the plan said before. It is the only squad write:
 * nothing takes a member out of a squad. */
export function assignSquad(idx: number, publicId: string): Promise<{ ok: boolean }> {
  return call<{ ok: boolean }>(
    'PUT', `/api/clan/squads/${idx}/members/${encodeURIComponent(publicId)}`);
}

// --- the Clan War registration (numbers.md §14.4, R1263, R1264) -------

/** The clanwar run a registration belongs to. Every instant is a unix
 *  second; `open` says the registration still takes a change. */
export interface ClanWarRun {
  id: number;
  label: string;
  opensAt: number;
  closesAt: number;
  cutoffAt: number;
  open: boolean;
}

/** The clan's registration for the run, and what the batch decided.
 *  `outcome` is empty until a batch has read the list, then `placed`,
 *  `no_opponent` or `unplaced_no_seat`. */
export interface ClanWarRegistration {
  members: string[];
  count: number;
  division: number;
  registeredAt: number;
  outcome: string;
  plays: number;
  world: number;
  banner: number;
}

/** `GET /api/clan/clanwar`. The division rule rides along, so the panel
 *  names the division a count takes without a copy of the figures. */
export interface ClanWar {
  run: ClanWarRun | null;
  registration: ClanWarRegistration | null;
  canRegister: boolean;
  divisionStep: number;
  divisionMin: number;
  divisionMax: number;
}

/** The next Clan War run and the clan's registration. */
export function getClanWar(): Promise<ClanWar> {
  return call<ClanWar>('GET', '/api/clan/clanwar');
}

/** Registers the clan, or replaces its member list. Owner or officer. */
export function registerClanWar(members: string[]): Promise<ClanWar> {
  return call<ClanWar>('PUT', '/api/clan/clanwar', { members });
}

/** Withdraws the registration before the cut-off. Owner or officer. */
export function withdrawClanWar(): Promise<ClanWar> {
  return call<ClanWar>('DELETE', '/api/clan/clanwar');
}

// --- the clan cup (clan-cup.md, R1629) -----------------------------------

/** The states of a clan cup. */
export type CupState = 'open' | 'drawn' | 'running' | 'done' | 'cancelled';

/** One clan of a cup. */
export interface CupClan {
  clanId: number;
  tag: string;
  name: string;
  seed?: number;
}

/** One cup: its calendar in unix seconds, its state and its title. */
export interface Cup {
  id: number;
  /** The ISO 8601 month, for example "2026-11". */
  month: string;
  /** 00:00 UTC of the cup day. */
  day: number;
  opensAt: number;
  cutoffAt: number;
  checkinOpensAt: number;
  checkinClosesAt: number;
  /** The start of each round of the cup bracket, the first round first. */
  rounds: number[];
  state: CupState;
  registered: number;
  clans: number;
  /** `too_few` or `ops` for a cancelled cup. */
  cancelReason?: string;
  winner: CupClan | null;
  runnerUpTag?: string;
  finalWins?: number;
  finalLosses?: number;
  titleAt?: number;
  markUntil?: number;
}

/** The figures of [meta.cup] the cards state. */
export interface CupRules {
  bracketSize: number;
  minClans: number;
  teamSize: number;
  reservesMax: number;
  roundBestOf: number;
  finalBestOf: number;
  noticeBeforeMin: number;
  /** The map pool, as `category/map id`. */
  maps: string[];
}

/** One member of a cup team. */
export interface CupMember {
  id: string;
  name: string;
  reserve: boolean;
}

/** The clan's cup team. */
export interface CupEntry {
  members: CupMember[];
  registeredAt: number;
  seed: number;
  checkedInAt: number;
  checkedInBy?: string;
  /** The round the team lost in, or 0. */
  outRound: number;
}

/** One side of a match. A clan id of 0 is a bye or a side with no team. */
export interface CupSide {
  clanId: number;
  tag: string;
  name: string;
  seed: number;
  /** The team missed the check-in. */
  absent?: boolean;
}

/** One match of the cup bracket. A side that is not known yet is null. */
export interface CupMatch {
  round: number;
  slot: number;
  a: CupSide | null;
  b: CupSide | null;
  bestOf: number;
  winsA: number;
  winsB: number;
  state: 'waiting' | 'live' | 'done';
  /** 1 for side A, 2 for side B, 0 for none. */
  winner: number;
  outcome?: string;
  /** The replay code of the game that runs now, for WATCH. */
  replay?: string;
  game?: number;
}

/** The clan's match of now: the match it plays, or the next one. */
export interface CupPlay {
  match: CupMatch;
  /** The hosting code a member of the team joins with, while the game waits. */
  hostingCode?: string;
  startBy?: number;
}

/** One cup of the CUP RECORD. */
export interface CupRecordRow {
  month: string;
  day: number;
  state: CupState;
  won: boolean;
  outRound: number;
  seed: number;
  finalWins?: number;
  finalLosses?: number;
  runnerUpTag?: string;
}

/** The CLAN CUP card: `GET /api/clan/cup`. */
export interface ClanCup {
  cup: Cup;
  next: Cup;
  rules: CupRules;
  entry: CupEntry | null;
  canRegister: boolean;
  canCheckIn: boolean;
  play: CupPlay | null;
  /** The cup the clan holds the cup mark of, or null. */
  title: Cup | null;
  record: CupRecordRow[];
  titles: number;
  /** The cup clock at the answer, in unix seconds (clan-cup.md §9). */
  now: number;
}

/** One cup of the strip of cup Saturdays. */
export interface CupStrip {
  month: string;
  day: number;
  state: CupState;
  winnerTag?: string;
}

/** The CUP page: `GET /api/cup`. */
export interface CupPage {
  cup: Cup;
  rules: CupRules;
  entries: CupClan[];
  matches: CupMatch[];
  strip: CupStrip[];
  /** The reader's own clan, or 0. */
  myClan: number;
  /** True when the cup bracket is the draw as it would stand now. */
  preview?: boolean;
  /** The cup clock at the answer, in unix seconds. */
  now: number;
}

/** The clan that holds the cup mark now. */
export interface CupMark {
  clanId: number;
  tag: string;
  name: string;
  month: string;
  until: number;
}

/** The CLAN CUP card of the caller's clan. */
export function getClanCup(): Promise<ClanCup> {
  return call<ClanCup>('GET', '/api/clan/cup');
}

/** Registers the clan's cup team, or changes it. Owner or officer. */
export function registerClanCup(players: string[], reserves: string[]): Promise<ClanCup> {
  return call<ClanCup>('PUT', '/api/clan/cup', { players, reserves });
}

/** Withdraws the clan's cup team before the cut-off. Owner or officer. */
export function withdrawClanCup(): Promise<ClanCup> {
  return call<ClanCup>('DELETE', '/api/clan/cup');
}

/** Checks the cup team in. A member of the team, or an officer. */
export function checkInClanCup(): Promise<ClanCup> {
  return call<ClanCup>('POST', '/api/clan/cup/checkin');
}

/** The CUP page of the cup of now. */
export function getCupPage(): Promise<CupPage> {
  return call<CupPage>('GET', '/api/cup');
}

/** The clan that holds the cup mark now, or null. */
export function getCupMark(): Promise<CupMark | null> {
  return call<CupMark | null>('GET', '/api/cup/mark');
}

// --- the clan invite (player-reach.md §5.4, R1306) ---------------------

/**
 * Why the caller may not invite one pickup squad mate now. The empty string
 * means INVITE. The server computes it with the same rule the invite write
 * applies, so the card never offers an INVITE the server refuses.
 */
export type InviteReason =
  '' | 'no_clan' | 'no_right' | 'in_clan' | 'invited' | 'clan_full' | 'invite_cap';

/** One other human of the caller's last pickup squad. */
export interface PickupMate {
  /** The public id, which the invite route names. */
  id: string;
  name: string;
  mode: string;
  worldId: number;
  season: number;
  reason: InviteReason;
}

/** One open clan invite of the caller. */
export interface ClanInvite {
  id: number;
  clanId: number;
  clanName: string;
  clanTag: string;
  clanFaction: number;
  /** The officer who sent it. Absent when that account is gone. */
  invitedBy?: string;
  createdAt: number;
  expiresAt: number;
}

/** The other humans of the caller's last pickup squad of each mode. */
export async function getPickupSquad(): Promise<PickupMate[]> {
  const out = await call<{ mates?: PickupMate[] }>('GET', '/api/pickup-squad');
  return out.mates ?? [];
}

/** Invites one pickup squad mate to the caller's clan. Officer or owner. */
export function inviteToClan(publicId: string): Promise<{ invite: ClanInvite }> {
  return call<{ invite: ClanInvite }>('POST', `/api/clan/invites/${encodeURIComponent(publicId)}`);
}

/** The caller's open clan invites, oldest first. */
export async function getInvites(): Promise<ClanInvite[]> {
  const out = await call<{ invites?: ClanInvite[] }>('GET', '/api/invites');
  return out.invites ?? [];
}

/** Joins the clan of one invite. */
export function acceptInvite(id: number): Promise<{ ok: boolean }> {
  return call<{ ok: boolean }>('POST', `/api/invites/${id}/accept`);
}

/** Ends one invite. */
export function declineInvite(id: number): Promise<{ ok: boolean }> {
  return call<{ ok: boolean }>('POST', `/api/invites/${id}/decline`);
}

export {
  CODE_NOT_PICKUP_MATE, CODE_INVITEE_IN_CLAN, CODE_ALREADY_INVITED,
  CODE_INVITE_CAP, CODE_INVITE_EXPIRED,
} from '../gen/balance';

// --- the clan Discord webhook (player-reach.md §3.9, R1311) ---------------
//
// The owner or an officer reads and writes it; a member gets `forbidden`.
// The server never answers the stored URL: `url` is the masked form.

export { CODE_DISCORD_INVALID, CODE_DISCORD_NONE } from '../gen/balance';

/** The state of the webhook: none stored, on, or off after the refusals. */
export type DiscordState = 'none' | 'on' | 'off';

/** The webhook of the caller's clan, as `GET /api/clan/discord` answers. */
export interface ClanDiscord {
  configured: boolean;
  /** The masked URL. Absent while none is stored. */
  url?: string;
  lang: string;
  classes: string[];
  /** The Discord role id that a post of each class pings, by the class
   *  word (R1642). An older server sends none. */
  roles?: Record<string, string>;
  state: DiscordState;
  failures: number;
  offAt?: number;
  lastStatus: number;
  lastPostAt?: number;
}

/** What the TEST answered: whether Discord took the post, and its status. */
export interface DiscordTestResult {
  delivered: boolean;
  status: number;
  state: DiscordState;
}

/** The webhook of the caller's clan. */
export function getClanDiscord(): Promise<ClanDiscord> {
  return call<ClanDiscord>('GET', '/api/clan/discord');
}

/** Stores the webhook. An empty `url` keeps the stored one. `roles` is the
 *  whole set of role pings; an empty value clears the role of a class. */
export function putClanDiscord(
  body: { url: string; lang: string; classes: string[]; roles: Record<string, string>; on: boolean },
): Promise<ClanDiscord> {
  return call<ClanDiscord>('PUT', '/api/clan/discord', body);
}

/** Removes the webhook. */
export function deleteClanDiscord(): Promise<{ ok: boolean }> {
  return call<{ ok: boolean }>('DELETE', '/api/clan/discord');
}

/** Sends one test post now. */
export function testClanDiscord(): Promise<DiscordTestResult> {
  return call<DiscordTestResult>('POST', '/api/clan/discord/test');
}

// --- the report and the block list (deployment.md §7.6) ------------------

/** One reason class of a report, from data/product/meta.toml. */
export type ReportReason = (typeof REPORT_REASONS)[number];

/**
 * What a report names. The kind says which of the other fields the server
 * reads: a chat line by `seq` in the caller's own clan channel, an account
 * name by `account`, a clan by `clan`, a captain by `account`, `roster`
 * and `captain`, a fleet name pool by `account`, a theme by `theme` and
 * `category`. The server copies the content itself.
 */
export interface ReportTarget {
  kind: 'chat' | 'account' | 'clan' | 'captain' | 'fleet' | 'theme' | 'map';
  account?: string;
  clan?: number;
  seq?: number;
  roster?: string;
  captain?: number;
  theme?: string;
  category?: string;
}

/**
 * Files one report. The answer is the same 202 for a new report and for a
 * second report of the same content, and it says nothing more.
 */
export function postReport(target: ReportTarget, reason: ReportReason, note: string): Promise<{ received: boolean }> {
  return call<{ received: boolean }>('POST', '/api/reports', { ...target, reason, note });
}

/** A report topic of the REPORT button (meta.toml [meta.player_reports]). */
export type PlayerReportTopic = (typeof PLAYER_REPORT_TOPICS)[number];

/**
 * One player report, as the REPORT sheet fills it (fault-reports.md §4,
 * R1583). Every part but the topic and the text is optional.
 */
export interface PlayerReportDraft {
  topic: PlayerReportTopic;
  text: string;
  /** The end of the client log. The sender keeps its last runes. */
  logTail?: string;
  world?: number;
  tick?: number;
  /** The renderer backend, `webgpu` or `webgl2`. */
  renderer?: string;
  fleets?: number[];
  page?: string;
  build?: string;
  /** One PNG image. The sheet scales it to fit the cap. */
  screenshot?: Uint8Array;
}

/** What the meta server answers, and whether the sender left the
 *  screenshot out because it was over the cap. `ref` is the report
 *  reference the sheet shows, `R-7F3K2Q` (fault-reports.md §4.5, R1622). */
export interface PlayerReportReceipt {
  id: number;
  ref: string;
  shotDropped: boolean;
}

/**
 * Sends one player report (`POST /api/player-reports`, fault-reports.md
 * §4, R1583). It cuts the text and the start of the log tail to the caps
 * of `[meta.player_reports]` and the fleets to their count, so the meta
 * server refuses only a part the sheet could not fit; a screenshot over
 * the cap is left out, and the receipt says so. The route needs a session.
 */
export async function sendPlayerReport(d: PlayerReportDraft): Promise<PlayerReportReceipt> {
  const log = Array.from(d.logTail ?? '');
  const shot = d.screenshot;
  const shotDropped = shot !== undefined && shot.byteLength > PLAYER_REPORT_SCREENSHOT_MAX_BYTES;
  const body = {
    topic: d.topic,
    text: cutRunes(d.text.trim(), PLAYER_REPORT_TEXT_MAX_RUNES),
    logTail: log.slice(Math.max(0, log.length - PLAYER_REPORT_LOG_TAIL_MAX_RUNES)).join(''),
    world: d.world ?? 0,
    tick: d.tick ?? 0,
    renderer: d.renderer ?? '',
    fleets: (d.fleets ?? []).slice(0, PLAYER_REPORT_FLEET_IDS_MAX),
    page: d.page ?? '',
    build: d.build ?? '',
    screenshot: shot !== undefined && !shotDropped ? base64Of(shot) : '',
  };
  const res = await call<{ id?: unknown; ref?: unknown }>('POST', '/api/player-reports', body);
  return { id: num(res.id), ref: str(res.ref), shotDropped };
}

/** One account on the caller's block list. */
export interface BlockedAccount {
  /** The public id, which the lift route names. */
  id: string;
  name: string;
  /** When the block was set, in unix seconds. */
  at: number;
}

/** The block list and its cap. */
export interface BlockList {
  blocks: BlockedAccount[];
  max: number;
}

/** The caller's block list, newest first. */
export function getBlocks(): Promise<BlockList> {
  return call<BlockList>('GET', '/api/blocks');
}

/** Blocks one account: the clan chat drops its lines for the caller. */
export function putBlock(publicId: string): Promise<BlockList> {
  return call<BlockList>('PUT', `/api/blocks/${encodeURIComponent(publicId)}`);
}

/** Lifts one block. */
export function deleteBlock(publicId: string): Promise<BlockList> {
  return call<BlockList>('DELETE', `/api/blocks/${encodeURIComponent(publicId)}`);
}

export { CODE_BLOCK_CAP, CODE_CHAT_MUTED } from '../gen/balance';

// --- the chat relay (player-reach.md §3.10, R1641) -------------------------
//
// The owner or an officer reads and writes it; a member gets `forbidden`.
// The server never answers the stored URL: `url` is the masked form. A
// refused body takes `discord_invalid`, as the clan webhook does, and the
// TEST answers the shape of the webhook TEST.

export { CODE_RELAY_NONE } from '../gen/balance';

/** The chat relay of the caller's clan, as `GET /api/clan/discord/relay`
 *  answers. `state` is off after the switch or after the refusals. */
export interface ChatRelay {
  configured: boolean;
  /** The masked URL. Absent while none is stored. */
  url?: string;
  lang: string;
  state: DiscordState;
  failures: number;
  offAt?: number;
  lastStatus: number;
  lastPostAt?: number;
}

/** The chat relay of the caller's clan. */
export function getChatRelay(): Promise<ChatRelay> {
  return call<ChatRelay>('GET', '/api/clan/discord/relay');
}

/** Stores the relay and its switch. An empty `url` keeps the stored one. */
export function putChatRelay(body: { url: string; lang: string; on: boolean }): Promise<ChatRelay> {
  return call<ChatRelay>('PUT', '/api/clan/discord/relay', body);
}

/** Removes the relay. */
export function deleteChatRelay(): Promise<{ ok: boolean }> {
  return call<{ ok: boolean }>('DELETE', '/api/clan/discord/relay');
}

/** Sends one test post of the relay now. */
export function testChatRelay(): Promise<DiscordTestResult> {
  return call<DiscordTestResult>('POST', '/api/clan/discord/relay/test');
}

// --- the hub's read routes (R229) ------------------------------
//
// Everything below is a read the hub draws a panel from. Some of them
// answer real rows, some answer an empty table until a generator lands, and
// two answer a typed refusal on a deployment that carries no shop and no
// modding tools. The hub renders each of those states honestly and invents
// nothing: an empty list is a true answer, and a 501 is a sentence a person
// may read.

/**
 * The codes the hub's read routes answer with (R341, generated).
 *
 * `shop_disabled` is `GET /api/shop/*` on a deployment with no Paddle
 * configuration; `not_implemented` is a route with a specification and no
 * implementation behind it; `entitlement_required` is an account that
 * holds no grant the route needs; `registration_required` is a guest that
 * must save the account before it buys anything.
 */
export {
  CODE_SHOP_DISABLED, CODE_NOT_IMPLEMENTED, CODE_ENTITLEMENT,
  CODE_REGISTRATION_REQUIRED, CODE_NOT_FOUND,
} from '../gen/balance';

/**
 * The entitlement keys the hub gates a card on. The catalog is
 * `data/contracts/entitlements.toml`, and balancegen writes both this half
 * and the server's `internal/entitlements` from it (R340), so a key is
 * spelled once. The account's own set arrives in `Me.entitlements`.
 *
 * The whole catalog stands in `gen/balance.ts`; these are the keys a panel
 * names today, re-exported so no import site changes. The bit each key
 * occupies is the server's business and never reaches the client.
 */
export {
  KEY_SUPPORTER, KEY_PREMIUM, KEY_EVENT, KEY_CLANWAR,
  KEY_CAPTAIN_DESIGNER, KEY_FLEET_DESIGNER, KEY_CHRONICLE, KEY_MODS,
  KEY_UI_THEMES, KEY_THEME_MAKER, KEY_SABBATICAL,
} from '../gen/balance';

/** One article. `body` is Markdown and it travels as data: the meta server
 * never renders it, and the client must never put it in the document as
 * HTML (R154). */
export interface NewsItem {
  slug: string;
  title: string;
  body: string;
  bodyFormat: string;
  /** Unix seconds, wall clock. Absent on a draft, which the public route
   * never serves. */
  publishedAt?: number;
  updatedAt: number;
}

/** One page of the news list, newest first. */
export interface NewsPage {
  items: NewsItem[];
  total: number;
  limit: number;
  offset: number;
}

/** The public news list. It needs no session. */
export async function getNews(limit = 10, offset = 0): Promise<NewsPage> {
  const out = await call<NewsPage>('GET', `/api/news?limit=${limit}&offset=${offset}`);
  return { items: out.items ?? [], total: out.total ?? 0, limit, offset };
}

/** One line of the Honour ladder. */
export interface HonourRow {
  rank: number;
  accountId: string;
  name: string;
  honour: number;
  seasons: number;
  /** The English rank title of the lifetime Honour. Only the live CORE
   *  ladder states it (R3234); the hub writes it through chronicleNoun. */
  title?: string;
}

/** The live Honour ladder of one mode. */
export interface HonourBoard {
  board: string;
  mode: string;
  /** Unix seconds, wall clock. */
  takenAt: number;
  rows: HonourRow[];
}

export async function getHonourBoard(mode = 'core', limit = 50): Promise<HonourBoard> {
  const out = await call<HonourBoard>(
    'GET', `/api/leaderboards/honour?mode=${encodeURIComponent(mode)}&limit=${limit}`);
  return { ...out, rows: out.rows ?? [] };
}

/** One line of the Clan Honour ladder of clans (R1512). */
export interface ClanHonourRow {
  rank: number;
  clanId: number;
  name: string;
  tag: string;
  honour: number;
  /** The Clan Wars the clan's ledger holds a row for. */
  wars: number;
}

/** The live Clan Honour ladder of clans. */
export interface ClanHonourBoard {
  /** Unix seconds, wall clock. */
  takenAt: number;
  rows: ClanHonourRow[];
}

export async function getClanHonourBoard(limit = 50): Promise<ClanHonourBoard> {
  const out = await call<ClanHonourBoard>('GET', `/api/leaderboards/clans?limit=${limit}`);
  return { ...out, rows: out.rows ?? [] };
}

/** The reader's figure and place on one board; place 0 means that the
 *  board does not hold the reader (R1907). */
export interface MyPlace {
  place: number;
  honour: number;
}

/** One seat of the reader: the place and the score of a closed season,
 *  0 and 0 for an open one. */
export interface MySeasonPlace {
  worldId: number;
  season: number;
  closed: boolean;
  place: number;
  score: number;
}

/** The reader's own place on every board of RANKS, from
 *  `GET /api/leaderboards/me` (R1907, deployment.md §7). The ladder entry
 *  states the tier (1 for the lowest) and never the ladder number (R1619). */
export interface MyPlaces {
  guest: boolean;
  honour: { core: MyPlace; event: MyPlace; clanwar: MyPlace };
  clan: { clanId: number; tag: string; place: number; honour: number } | null;
  seasons: MySeasonPlace[];
  ladder: { guest: boolean; rated: boolean; place: number; tier: number };
}

export async function getMyPlaces(): Promise<MyPlaces> {
  type Wire = Partial<Omit<MyPlaces, 'honour' | 'ladder'>> & {
    honour?: Partial<Record<'core' | 'event' | 'clanwar', Partial<MyPlace>>>;
    ladder?: Partial<MyPlaces['ladder']>;
  };
  const out = await call<Wire>('GET', '/api/leaderboards/me');
  const one = (p?: Partial<MyPlace>): MyPlace => ({ place: p?.place ?? 0, honour: p?.honour ?? 0 });
  return {
    guest: out.guest ?? false,
    honour: { core: one(out.honour?.core), event: one(out.honour?.event), clanwar: one(out.honour?.clanwar) },
    clan: out.clan ?? null,
    seasons: out.seasons ?? [],
    ladder: {
      guest: out.ladder?.guest ?? false, rated: out.ladder?.rated ?? false,
      place: out.ladder?.place ?? 0, tier: out.ladder?.tier ?? 0,
    },
  };
}

/** One line of a closed season's table. */
export interface SeasonRow {
  rank: number;
  accountId: string;
  name: string;
  score: number;
  honour: number;
}

/** One world's closed season, read from its stored report. */
export interface SeasonBoard {
  world: number;
  season: number;
  mode: string;
  closedAt: number;
  rows: SeasonRow[];
}

export async function getSeasonBoard(world: number, season: number): Promise<SeasonBoard> {
  const out = await call<SeasonBoard>(
    'GET', `/api/leaderboards/season?world=${world}&season=${season}`);
  return { ...out, rows: out.rows ?? [] };
}

/**
 * One history card: one closed season of one mode. `payload` is the board
 * of the Honour that season paid, which the server sums from its ledger
 * (R1599), and the hub reads its rows where they are there. The shape
 * inside is the server's own board and not a wire type, so it arrives
 * unparsed.
 */
export interface BoardSnapshot {
  board: string;
  mode: string;
  season: number;
  takenAt: number;
  payload: unknown;
}

/** The closed seasons with the Honour each one paid, newest first. */
export async function getBoardHistory(mode = '', limit = 50): Promise<BoardSnapshot[]> {
  const q = mode === '' ? `?limit=${limit}` : `?mode=${encodeURIComponent(mode)}&limit=${limit}`;
  const out = await call<{ snapshots?: BoardSnapshot[] }>('GET', `/api/leaderboards/history${q}`);
  return out.snapshots ?? [];
}

/** One memorial pool entry, as the public board reads it (R1243). No
 *  account and no note leave the meta server on this route. */
export interface MemorialEntry {
  name: string;
  world: string;
  season: number;
  faction: number;
  score: number;
  /** How many maps the meta server served the name to. */
  shown: number;
}

/** The active memorial pool entries, newest season first. */
export async function getBoardMemorials(limit = 50): Promise<MemorialEntry[]> {
  const out = await call<{ memorials?: MemorialEntry[] }>(
    'GET', `/api/leaderboards/memorials?limit=${limit}`);
  return out.memorials ?? [];
}

/**
 * One account's lifetime counters. The route is public and the table is
 * filled by a generator that is not built, so every counter is zero today
 * and the panel says so rather than drawing a made-up row.
 */
export interface AccountStats {
  accountId: string;
  name: string;
  kind: string;
  honourCore: number;
  honourEvent: number;
  /** The account's Clan Honour (R1512). */
  honourClanwar?: number;
  seasonsPlayed: number;
  bestScore: number;
  cellsTaken: number;
  fleetsLost: number;
  /** Unix seconds, wall clock. Absent while nothing has ever written. */
  updatedAt?: number;
}

export function getStats(publicId: string): Promise<AccountStats> {
  return call<AccountStats>('GET', `/api/stats/${encodeURIComponent(publicId)}`);
}

// ---------------------------------------------------------------------------
// The achievement record (achievements.md §5, §7, §8)
// ---------------------------------------------------------------------------
//
// Four routes, all on `aquametad`, which calls `aquaprogressd` behind them.
// A meta server that holds no progress block answers 503
// `progress_unavailable` on every one of them, and the hub draws its
// honest failed state from that. It never draws a zero: a zero nobody
// measured reads as a result.
//
// The catalog itself is NOT read from a route. It rides the client bundle
// (`gen/presentation.ts`, `ACHIEVEMENTS`), so a page knows every code, its
// category and its ladder with no call. The routes carry the one thing the
// bundle cannot know: what THIS account holds.
//
// Every answer is read defensively, the way `readEffects` is: the payload
// is a stored record and not a wire type, so a missing field reads as an
// empty list and never as a crash.

/** One row of the catalog, as the public route filters it for the caller. */
export interface Achievement {
  code: string;
  kind: string;
  category: string;
  mode: string;
  hidden: boolean;
}

/** One grade the account holds. `reachedAt` is a unix second, wall clock. */
export interface AchievementGrade {
  code: string;
  grade: number;
  reachedAt: number;
}

/** One recognition the account holds. It carries no grade and no points. */
export interface AchievementRecognition {
  id: string;
  code: string;
  world: number;
  season: number;
  mode: string;
  source: string;
  reason: string;
  grantedAt: number;
}

/** The season scope of one account: which season, and its own totals. */
export interface AchievementSeason {
  mode: string;
  world: number;
  season: number;
  totals: Record<string, number>;
}

/** What one account holds, as `/api/achievements/{publicId}` states it. */
export interface AccountAchievements {
  points: number;
  tier: string;
  grades: AchievementGrade[];
  recognitions: AchievementRecognition[];
  /** Metric code to the lifetime value. */
  totals: Record<string, number>;
  /** The season scope, or absent where the account plays no season. */
  season?: AchievementSeason;
}

/** One line of an achievement board. */
export interface AchievementBoardRow {
  place: number;
  publicId: string;
  name: string;
  points: number;
  grades: number;
  self: boolean;
}

/** One achievement board: lifetime or season, free or premium. */
export interface AchievementBoard {
  board: string;
  tier: string;
  /** Unix seconds, wall clock. The snapshot's own instant. */
  takenAt: number;
  rows: AchievementBoardRow[];
}

/** Which board to read. */
export type AchievementBoardKind = 'life' | 'season';
/** Which half of a board to read. Supporter shares the free board. */
export type AchievementTier = 'free' | 'premium';

function readAccountAchievements(out: Partial<AccountAchievements>): AccountAchievements {
  return {
    points: num(out.points),
    tier: str(out.tier),
    grades: Array.isArray(out.grades) ? out.grades : [],
    recognitions: Array.isArray(out.recognitions) ? out.recognitions : [],
    totals: out.totals ?? {},
    season: out.season,
  };
}

function readAchievementBoard(out: Partial<AchievementBoard>): AchievementBoard {
  return {
    board: str(out.board),
    tier: str(out.tier),
    takenAt: num(out.takenAt),
    rows: Array.isArray(out.rows) ? out.rows : [],
  };
}

/**
 * Which season the `season` half of a record belongs to (R951).
 *
 * The service keys a season total on the mode, the world and the season
 * number together, so a call that names none of them answers empty season
 * totals and every journey row reads "NEXT AT" with no "NOW". The caller
 * names the scope because the meta server holds several placements per
 * account and cannot guess which one a page is looking at.
 */
export interface AchievementScope {
  /** The world's mode, as `modes.toml` spells it: `core`, `event`, … */
  mode: string;
  /** The world's id. */
  world: number;
  /** The season number that world stands in. */
  season: number;
}

/** The three query keys of one scope, or none for a lifetime-only read. */
function achievementScopeQuery(scope?: AchievementScope): string {
  if (!scope) return '';
  return `?mode=${encodeURIComponent(scope.mode)}`
    + `&world=${scope.world}&season=${scope.season}`;
}

/** What this account holds: its grades, its recognitions and its totals.
 *  A scope names the season half; without one the season totals are empty
 *  and the lifetime totals still stand (R951). */
export async function getMyAchievements(
  scope?: AchievementScope,
): Promise<AccountAchievements> {
  return readAccountAchievements(await call<Partial<AccountAchievements>>(
    'GET', `/api/achievements/me${achievementScopeQuery(scope)}`));
}

/** One achievement board, newest snapshot. */
export async function getAchievementBoard(
  board: AchievementBoardKind, tier: AchievementTier, limit = 50,
): Promise<AchievementBoard> {
  const q = `board=${encodeURIComponent(board)}&tier=${encodeURIComponent(tier)}&limit=${limit}`;
  return readAchievementBoard(await call<Partial<AchievementBoard>>(
    'GET', `/api/leaderboards/achievements?${q}`));
}

// --- the season chronicle (chronicle.md §7) --------------------------------
//
// The meta server stores the facts of a page and never reads inside them;
// the client writes the prose (R1166). The volume list needs a session
// alone, so the locked RECORD card can say how many days are written. A
// read of the pages needs the `chronicle` key too (R1167): without it the
// server answers 403. The share switch refuses a live season with 409
// (R1168). The two refusals carry a bare string code, so a caller tests
// the status and not the code.

/** One volume of the book: one world and one season of this account. */
export interface ChronicleVolume {
  world: number;
  worldName: string;
  season: number;
  /** The day pages written, the season page not counted. */
  days: number;
  hasSeasonPage: boolean;
  /** The season still runs. A live volume cannot be shared. */
  live: boolean;
  shared: boolean;
}

/** One stored page. Day 0 is the season page (chronicle.md §3.1). */
export interface ChronicleBookPage {
  day: number;
  rank: number;
  score: number;
  /** The places climbed in the day: positive for a climb. */
  rankDelta: number;
  scoreDelta: number;
  /** Unix seconds, wall clock: when the meta server stored the page. */
  at: number;
  facts: ChronicleFact[];
}

/** The wardroom a volume reads its captain voice lines from: the roster of
 *  the mode of its world, as the meta server resolves it, and `none` for a
 *  mode with no wardroom (chronicle.md §4.3, R1244). */
export type ChronicleRoster = 'core' | 'event' | 'clanwar' | 'none';

const CHRONICLE_ROSTERS: readonly ChronicleRoster[] = ['core', 'event', 'clanwar', 'none'];

/** One volume with its pages: ascending day, the season page last. */
export interface ChronicleVolumePages {
  world: number;
  worldName: string;
  season: number;
  live: boolean;
  /** An answer without the word (an older server, the public read, a world
   *  the registry no longer holds) reads as `core`. */
  roster: ChronicleRoster;
  /** Absent on the public read. */
  shared?: boolean;
  /** The name of the commander. Only the public read sends it
   *  (chronicle.md §9.4). */
  commander?: string;
  /** The faction the commander carried in that world and season. Only the
   *  public read sends it; the reader marks it in the legend of the season
   *  timelapse (R1620). */
  faction?: number;
  pages: ChronicleBookPage[];
}

/** The status of a page read without the `chronicle` key. */
export const STATUS_CHRONICLE_KEY = 403;
/** The status of a share switch on a live season. */
export const STATUS_SEASON_LIVE = 409;
/** The status of a public read of a volume that is not shared, is live or
 *  does not exist: the three look the same (R1168). */
export const STATUS_CHRONICLE_NOT_SHARED = 404;

/** Reads one volume row. Pure, so a test can hand it the JSON. */
function readChronicleVolume(v: Partial<ChronicleVolume>): ChronicleVolume {
  return {
    world: num(v.world),
    worldName: str(v.worldName),
    season: num(v.season),
    days: num(v.days),
    hasSeasonPage: v.hasSeasonPage === true,
    live: v.live === true,
    shared: v.shared === true,
  };
}

function readBookPage(p: Partial<ChronicleBookPage>): ChronicleBookPage {
  const facts = Array.isArray(p.facts) ? p.facts : [];
  return {
    day: num(p.day),
    rank: num(p.rank),
    score: num(p.score),
    rankDelta: num(p.rankDelta),
    scoreDelta: num(p.scoreDelta),
    at: num(p.at),
    facts: facts.filter((f) => typeof f === 'object' && f !== null && typeof f.kind === 'string'),
  };
}

/** Reads one volume answer. Pure, so a test can hand it the JSON. */
export function readChronicleVolumePages(v: Partial<ChronicleVolumePages>): ChronicleVolumePages {
  const out: ChronicleVolumePages = {
    world: num(v.world),
    worldName: str(v.worldName),
    season: num(v.season),
    live: v.live === true,
    roster: oneOf(CHRONICLE_ROSTERS, v.roster) ?? 'core',
    pages: Array.isArray(v.pages) ? v.pages.map(readBookPage) : [],
  };
  if (typeof v.shared === 'boolean') out.shared = v.shared;
  if (typeof v.commander === 'string' && v.commander !== '') out.commander = v.commander;
  if (typeof v.faction === 'number' && v.faction > 0) out.faction = v.faction;
  return out;
}

/** The volumes of the signed-in account, newest first. It needs no key. */
export async function getMyChronicleVolumes(): Promise<ChronicleVolume[]> {
  const out = await call<{ volumes?: Partial<ChronicleVolume>[] }>(
    'GET', '/api/chronicle/me/seasons');
  return (out.volumes ?? []).map(readChronicleVolume);
}

/** The pages of one own volume. It needs the `chronicle` key. */
export async function getMyChronicle(world: number, season: number): Promise<ChronicleVolumePages> {
  return readChronicleVolumePages(await call<Partial<ChronicleVolumePages>>(
    'GET', `/api/chronicle/me/${world}/${season}`));
}

/** Marks one closed own volume shared or private, and answers the state
 *  the server stored. */
export async function setChronicleShared(
  world: number, season: number, shared: boolean,
): Promise<boolean> {
  const out = await call<{ shared?: boolean }>(
    'PUT', `/api/chronicle/me/${world}/${season}/share`, { shared });
  return out.shared === true;
}

/** A shared closed volume of another account. It needs no session. A
 *  volume that is not shared answers 404, the same as a missing one. */
export async function getSharedChronicle(
  publicId: string, world: number, season: number,
): Promise<ChronicleVolumePages> {
  return readChronicleVolumePages(await call<Partial<ChronicleVolumePages>>(
    'GET', `/api/chronicle/${encodeURIComponent(publicId)}/${world}/${season}`));
}

/**
 * The season timelapse of a shared closed volume (chronicle.md §10.5,
 * R1598): the byte string of the world socket's `TimelapseState`, which
 * `net/timelapse.ts` decodes. It needs no session. A volume that is not
 * shared, and a season that stored no frame, answer 404, which throws an
 * `ApiError` as every refusal does.
 */
export async function getSharedTimelapse(
  publicId: string, world: number, season: number,
): Promise<Uint8Array> {
  const path = `/api/chronicle/${encodeURIComponent(publicId)}/${world}/${season}/timelapse`;
  let out: { status: number; text: string; bytes: Uint8Array | null; retryAfter: string | null };
  try {
    out = await request(path, { credentials: 'include', headers: { [LANG_HEADER]: LOCALE } },
      async (res) => (res.ok
        ? { status: res.status, text: '', bytes: new Uint8Array(await res.arrayBuffer()), retryAfter: null }
        : { status: res.status, text: await res.text(), bytes: null,
          retryAfter: res.headers?.get('Retry-After') ?? null }));
  } catch (err) {
    throw transportError(err);
  }
  if (out.bytes === null) throw parseError(out.status, out.text, out.retryAfter);
  return out.bytes;
}

/** One captain of the durable roster, as a person reads it. The attributes
 * and the talents travel as opaque numbers: the game server is the only
 * thing that knows what one does. */
export interface WardroomCaptain {
  /** The game id of the captain, the key of the Captain Designer routes
   *  and of a chronicle fact's `cid` (R1240). */
  id: number;
  /** The custom name where the owner set one, else the world's name. */
  name: string;
  attrs: number[];
  talents: number[];
  /** A blitz world ages a captain by a fraction, so this is not a whole
   * number. A world plays with the figure rounded down. */
  seasonsServed: number;
  /** The age in whole years, which the server computes (numbers.md §12,
   * R1170): the recruit age plus the years of every season aged. */
  age: number;
  recruitAge: number;
  status: string;
  /** The frozen face (R1240): 0 derives the seed from the name and the
   *  talents. */
  portraitSeed: number;
  /** 0 derives the sex from the first name, 1 is male, 2 is female. */
  portraitGender: number;
  /** The owner's own words. Only the owner reads them (monetization.md §3.1). */
  backstory: string;
  voice: string[];
}

/** Fills the fields a server that predates the Captain Designer leaves out. */
function readWardroomCaptain(c: Partial<WardroomCaptain>): WardroomCaptain {
  return {
    id: c.id ?? 0,
    name: c.name ?? '',
    attrs: c.attrs ?? [],
    talents: c.talents ?? [],
    seasonsServed: c.seasonsServed ?? 0,
    age: c.age ?? 0,
    recruitAge: c.recruitAge ?? 0,
    status: c.status ?? '',
    portraitSeed: c.portraitSeed ?? 0,
    portraitGender: c.portraitGender ?? 0,
    backstory: c.backstory ?? '',
    voice: c.voice ?? [],
  };
}

/** One mode's roster. `core` serves every account; `event` draws the roster
 * the event worlds play with, which stays apart from the core one. */
export interface Wardroom {
  mode: string;
  roster: string;
  captains: WardroomCaptain[];
}

export async function getWardroom(mode = 'core'): Promise<Wardroom> {
  const out = await call<Partial<Wardroom>>('GET', `/api/wardroom?mode=${encodeURIComponent(mode)}`);
  return {
    mode: out.mode ?? mode, roster: out.roster ?? '',
    captains: (out.captains ?? []).map(readWardroomCaptain),
  };
}

/**
 * The captain looks one save sends (monetization.md §3.1, R1240). Every
 * member goes on every save. A seed of 0 or a gender of 0 keeps what the
 * server stored.
 */
export interface CaptainLooks {
  name: string;
  portraitSeed: number;
  portraitGender: number;
  backstory: string;
  voice: string[];
}

/** The path of one captain's looks. */
function looksPath(roster: string, id: number): string {
  return `/api/wardroom/${encodeURIComponent(roster)}/${id}/looks`;
}

/**
 * Saves the looks of one captain. It needs the `captain_designer` key. A
 * refusal of one member is an ApiError with the code
 * `CODE_INVALID_CAPTAIN_LOOKS`, and `field` and `reason` name the member.
 * The answer is the captain as `getWardroom` reads it.
 */
export async function putCaptainLooks(
  roster: string, id: number, looks: CaptainLooks,
): Promise<WardroomCaptain> {
  return readWardroomCaptain(await call<Partial<WardroomCaptain>>('PUT', looksPath(roster, id), looks));
}

/** Gives one captain its default looks again. It needs no key (R1241). */
export async function deleteCaptainLooks(roster: string, id: number): Promise<WardroomCaptain> {
  return readWardroomCaptain(await call<Partial<WardroomCaptain>>('DELETE', looksPath(roster, id)));
}

/** The fleet name pool as a route answers it (monetization.md §3.2,
 * R1249). The list is never absent: an account with no pool reads []. */
interface FleetNamesAnswer { names?: unknown }

/** One call of the fleet name pool route, and the pool it answers. */
async function fleetNames(method: string, body?: unknown): Promise<string[]> {
  return strList((await call<FleetNamesAnswer>(method, '/api/fleet-names', body)).names);
}

/** Reads the account's fleet name pool. It needs the session only, so a
 * lapsed key still shows the pool the world draws from (R1241). */
export function getFleetNames(): Promise<string[]> {
  return fleetNames('GET');
}

/**
 * Saves the account's fleet name pool whole. It needs the `fleet_designer`
 * key. A refused pool is an ApiError with the code
 * `CODE_INVALID_FLEET_NAMES`; `reason` is the token and the message names
 * the refused entry. The answer is the pool as the server stored it.
 */
export function putFleetNames(names: string[]): Promise<string[]> {
  return fleetNames('PUT', { names });
}

/** Clears the account's fleet name pool. It needs no key (R1241). */
export function resetFleetNames(): Promise<string[]> {
  return fleetNames('DELETE');
}

/** The livery as a route answers it (monetization.md §3.2, R1252). ''
 * is NONE. */
interface FleetLiveryAnswer { livery?: unknown }

/** One call of the livery route, and the code it answers. */
async function fleetLivery(method: string, body?: unknown): Promise<string> {
  return str((await call<FleetLiveryAnswer>(method, '/api/fleet-livery', body)).livery);
}

/** Reads the account's livery code, '' for NONE. It needs the session
 * only, so a lapsed key still shows the livery the world draws (R1241). */
export function getFleetLivery(): Promise<string> {
  return fleetLivery('GET');
}

/**
 * Saves one livery code of the list. It needs the `fleet_designer` key. A
 * code off the list is an ApiError with the code
 * `CODE_INVALID_FLEET_LIVERY`. NONE is `resetFleetLivery`.
 */
export function putFleetLivery(code: string): Promise<string> {
  return fleetLivery('PUT', { livery: code });
}

/** Sets the livery to NONE. It needs no key (R1241). */
export function resetFleetLivery(): Promise<string> {
  return fleetLivery('DELETE');
}

/** One product of the shop. The price is a string the server formatted:
 * the client never computes a price and never sends one. */
export interface ShopPlan {
  id: string;
  name: string;
  tier: string;
  priceId: string;
  displayPrice: string;
  interval?: string;
  description?: string;
  /** What the tier grants, in bit order. */
  entitlements: string[];
}

/** The whole shop. A deployment with no Paddle block answers 503 with
 * `shop_disabled`, and the panel says that plainly. */
export interface ShopPlans {
  environment: string;
  clientToken?: string;
  plans: ShopPlan[];
}

export async function getShopPlans(): Promise<ShopPlans> {
  const out = await call<ShopPlans>('GET', '/api/shop/plans');
  return { ...out, plans: out.plans ?? [] };
}

/** What the browser needs to open Paddle's checkout overlay. The meta
 * server creates nothing at Paddle: it states which price and which
 * account, and the webhook is the only thing that grants an entitlement. */
export interface Checkout {
  environment: string;
  clientToken?: string;
  priceId: string;
  customData: Record<string, string>;
  successUrl: string;
  cancelUrl: string;
  plan: ShopPlan;
}

export function startCheckout(plan: string): Promise<Checkout> {
  return call<Checkout>('POST', '/api/shop/checkout', { plan });
}

// --- themes (themes.md §11, R808, R813) -------------------------------------
//
// Every route needs the session cookie. A refusal arrives as the typed
// ApiError of every other route, and `field` and `reason` carry the detail
// of a theme refusal: 404 `not_found` for a code with no theme of the
// category or a malformed code, 403 `entitlement_required` for a missing
// key (`ui_themes` to look up and to link, `theme_maker` to save), 403
// `impersonation_refused` for a save by an operator, 429 `rate_limited` for
// the lookup limit, 400 `invalid_theme`, `unknown_code` or `bad_request`,
// and 413 `theme_too_large`.
//
// **One code per creator** (R819): every theme an account makes carries the
// account's one code, so a theme is found by the code AND its category.

/** The theme refusals a surface switches on (generated, R341). */
export {
  CODE_INVALID_THEME, CODE_THEME_TOO_LARGE, CODE_UNKNOWN_CODE, CODE_BAD_REQUEST,
} from '../gen/balance';

/** One custom theme, as `GET /api/themes/{code}/{category}` and the save
 *  answer it. */
export type ThemeRecord = Omit<CachedTheme, 'missing'>;

/** One custom theme of the account, with how many accounts link it. */
export interface MyTheme {
  code: string;
  category: ThemeCategory;
  version: number;
  body: unknown;
  /** How many accounts link the theme, the maker included. */
  links: number;
  /** How many accounts other than the maker link it: the accounts a save
   *  reaches (themes.md §6.3, R2116). */
  others?: number;
  updatedAt: number;
}

/** The account's theme links: category → theme code. */
export type ThemeLinks = Partial<Record<ThemeCategory, string>>;

/** Looks up the custom theme one code names in one category. It needs
 *  `ui_themes`. A code with no theme of the category answers 404. */
export function getTheme(code: string, category: ThemeCategory): Promise<ThemeRecord> {
  return call<ThemeRecord>('GET',
    `/api/themes/${encodeURIComponent(code)}/${encodeURIComponent(category)}`);
}

/** The account's one theme code, or null before its first theme, and the
 *  custom themes it made, one per category at most (R819). */
export interface MyThemes {
  code: string | null;
  themes: MyTheme[];
}

/** The custom themes this account made, and the code they share. */
export async function getMyThemes(): Promise<MyThemes> {
  const out = await call<{ code?: string | null; themes?: MyTheme[] }>('GET', '/api/themes/mine');
  return { code: out.code ?? null, themes: out.themes ?? [] };
}

/** Makes or updates the account's custom theme of one category. It needs
 *  `theme_maker`. The code of an existing theme stays. */
export function putTheme(category: ThemeCategory, body: unknown): Promise<ThemeRecord> {
  return call<ThemeRecord>('PUT', `/api/themes/${encodeURIComponent(category)}`, { body });
}

/**
 * Changes the account's theme links. Only the categories present change: a
 * code links that theme, and null removes the link. The answer is every
 * link the account holds after the change.
 */
export async function putThemeLinks(
  links: Partial<Record<ThemeCategory, string | null>>,
): Promise<ThemeLinks> {
  const out = await call<{ links?: ThemeLinks }>('PUT', '/api/themes/links', { links });
  return out.links ?? {};
}

// ---------------------------------------------------------------------------
// The effect grants and the effect picks (effects.md §3, R908, R909)
// ---------------------------------------------------------------------------
//
// The two routes carry one shape. `granted` is what the account holds, one
// entry per effect grant; `picks` is the account's pick per slot, slot →
// effect code; and `slots` is every slot the server spells, in its own
// order. Any tier picks, so neither route reads an entitlement.
//
// A PUT carries the WHOLE map, because it replaces the stored picks: a map
// that leaves a slot out clears that slot. A refusal is a 400 with a code —
// the effect is not granted, the slot refuses the effect, or the server
// spells no such slot — and the stored picks do not change.

/** One effect grant of the account. `expiresAt` is a unix second, and 0
 *  says the grant does not end. `source` names what gave it. */
export interface EffectGrant {
  code: string;
  wire: number;
  expiresAt: number;
  source: string;
}

/** What the account holds and what it wears, as both effect routes state it. */
export interface AccountEffects {
  granted: EffectGrant[];
  /** slot → effect code. A slot with no pick is not in the map. */
  picks: Record<string, string>;
  /** Every slot the server spells, in its own order. */
  slots: string[];
}

function readEffects(out: Partial<AccountEffects>): AccountEffects {
  return { granted: out.granted ?? [], picks: out.picks ?? {}, slots: out.slots ?? [] };
}

/** The effect grants of the account and its pick per slot. */
export async function getEffects(): Promise<AccountEffects> {
  return readEffects(await call<Partial<AccountEffects>>('GET', '/api/effects'));
}

/** Replaces the account's effect picks with `picks`, whole. The answer is
 *  what the account holds and wears after the change. */
export async function putEffectPicks(picks: Record<string, string>): Promise<AccountEffects> {
  return readEffects(await call<Partial<AccountEffects>>('PUT', '/api/effects', { picks }));
}

// ---------------------------------------------------------------------------
// Arcade match replays (arcade.md §15.4, §15.5)
// ---------------------------------------------------------------------------

/** One seat of a match, as the public replay route states it. It carries
 *  the display name and no account id (R902). */
export interface ReplayPlayer {
  slot: number;
  team: number;
  name: string;
  bot: boolean;
}

/** The map a match was fought on. */
export interface ReplayMap {
  id: string;
  name: string;
  revision: number;
  category: string;
}

/**
 * One match, as `GET /api/arcade/replays/{code}` states it.
 *
 * It needs no session: a share link must open for anybody the reader hands
 * it to, and every miss is one 404, so the route cannot be walked for
 * codes. A finished match carries `fileUrl` alone. A live match carries
 * both: `wsUrl` is how a reader watches it, and `fileUrl` is how a reader
 * who joined late fetches the minutes it opened after (R921).
 *
 * Every field is optional on the way in and filled in here, because a meta
 * server older than this client answers a smaller object and the page must
 * still draw a cover.
 */
export interface ReplayInfo {
  code: string;
  live: boolean;
  map: ReplayMap;
  hosted: boolean;
  recordInput: boolean;
  seats: number;
  humans: number;
  /** Unix seconds. 0 where the server named none. */
  startedAt: number;
  endedAt: number;
  /** WALL seconds of fighting (R1112). The page states it as it is. */
  elapsedSec: number;
  verdict: string;
  winTeam: number;
  reason: string;
  players: ReplayPlayer[];
  /** The file, on a finished match. */
  fileUrl: string;
  /** The spectator socket, on a live one. */
  wsUrl: string;
  /** Unix seconds: when the record is deleted. 0 where none was named. */
  keepUntil: number;
  /** How far behind a spectator's picture runs, in seconds. 0 on a
   *  finished match, and 0 where the server named none. */
  spectateDelaySec: number;
}

/** The shape the route answers with, before this module fills it in. */
type ReplayInfoWire = Partial<Omit<ReplayInfo, 'map' | 'players'>>
& { map?: Partial<ReplayMap>; players?: Partial<ReplayPlayer>[] };

/** Fills every absent field. The parsing is deliberately tolerant: the
 *  page draws a cover from whatever the server named. */
function readReplayInfo(out: ReplayInfoWire): ReplayInfo {
  return {
    code: out.code ?? '',
    live: out.live ?? false,
    map: {
      id: out.map?.id ?? '',
      name: out.map?.name ?? '',
      revision: out.map?.revision ?? 0,
      category: out.map?.category ?? '',
    },
    hosted: out.hosted ?? false,
    recordInput: out.recordInput ?? false,
    seats: out.seats ?? 0,
    humans: out.humans ?? 0,
    startedAt: out.startedAt ?? 0,
    endedAt: out.endedAt ?? 0,
    elapsedSec: out.elapsedSec ?? 0,
    verdict: out.verdict ?? '',
    winTeam: out.winTeam ?? 0,
    reason: out.reason ?? '',
    players: (out.players ?? []).map((p) => ({
      slot: p.slot ?? 0, team: p.team ?? 0, name: p.name ?? '', bot: p.bot ?? false,
    })),
    fileUrl: out.fileUrl ?? '',
    wsUrl: out.wsUrl ?? '',
    keepUntil: out.keepUntil ?? 0,
    spectateDelaySec: out.spectateDelaySec ?? 0,
  };
}

/**
 * One match by its replay code. No session, and one 404 for every miss.
 *
 * The route is rate limited per address, so a caller that reads
 * `CODE_TOO_MANY_REPLAY_LOOKUPS` must wait rather than ask again at once.
 */
export async function arcadeReplay(code: string): Promise<ReplayInfo> {
  const out = await call<ReplayInfoWire>(
    'GET', `/api/arcade/replays/${encodeURIComponent(code)}`,
  );
  return readReplayInfo(out);
}

/** One row of the caller's own match history: a `ReplayInfo` with the
 *  three facts only the caller's session can answer. */
export interface HistoryRow extends ReplayInfo {
  /** The seat this account held, or 0 where the row names none. */
  mySlot: number;
  myTeam: number;
  /** The server computes it, so the client prints the word and decides
   *  nothing: won, lost, draw, void or live. */
  outcome: string;
}

/** One page of the caller's match history. */
export interface History {
  matches: HistoryRow[];
  /** The cursor for the next page, or '' at the end. */
  next: string;
  limit: number;
  /** How many days a record is kept. The panel prints it. */
  keepDays: number;
  /** How many matches the record keeps for the caller, on the first page
   *  only (R1948); undefined on a later page and from an older server. */
  played?: number;
}

/**
 * The caller's own matches, newest first (arcade.md §15.4).
 *
 * It needs a session, and a guest holds one too: a guest plays matches and
 * must be able to find them again. `before` is the cursor of the previous
 * page, as that page's `next` named it.
 */
export async function arcadeHistory(opts: { limit?: number; before?: string } = {}):
Promise<History> {
  const out = await call<{
    matches?: (ReplayInfoWire & Partial<Pick<HistoryRow, 'mySlot' | 'myTeam' | 'outcome'>>)[];
    next?: string; limit?: number; keepDays?: number; played?: number;
  }>('GET', withQuery('/api/arcade/history', { limit: opts.limit, before: opts.before }));
  return {
    matches: (out.matches ?? []).map((row) => ({
      ...readReplayInfo(row),
      mySlot: row.mySlot ?? 0,
      myTeam: row.myTeam ?? 0,
      outcome: row.outcome ?? '',
    })),
    next: out.next ?? '',
    limit: out.limit ?? 0,
    keepDays: out.keepDays ?? 0,
    ...(out.played !== undefined ? { played: out.played } : {}),
  };
}

// ---- the arcade ladder (arcade.md §18, R1600) ----
//
// Three reads of the meta server. The client computes nothing: the meta
// server rates the matches, writes the ladder number (the rating less two
// deviations) and counts the place. The ladder tier is not ruled yet, so
// no tier is read here; the UI lane reads it from the ladder number.

/** The outcome word of one rated match: the words of the match history. */
export type LadderOutcome = 'won' | 'lost' | 'draw';

/** One rated match of a ladder card, newest first. */
export interface LadderMatch {
  /** Unix seconds, wall clock: the verdict. */
  at: number;
  category: string;
  mapId: string;
  /** The replay code, or '' where the match was not recorded. */
  code: string;
  outcome: LadderOutcome;
  /** The ladder number before and after the match. */
  before: number;
  after: number;
}

/** One account's ladder card: the RECORD card of round 078. */
export interface LadderCard {
  accountId: string;
  name: string;
  /** A guest has no ladder (R1603); its card holds the start figures. */
  guest: boolean;
  /** False until the account plays its first rated match. */
  rated: boolean;
  rating: number;
  deviation: number;
  volatility: number;
  /** The ladder number, and the highest it reached. */
  ladder: number;
  best: number;
  matches: number;
  wins: number;
  losses: number;
  draws: number;
  /** Unix seconds of the last rated match, or 0 before the first. */
  lastAt: number;
  /** The place on the ladder list, or 0 while the list does not show
   *  the account (no rated match in the list's window). */
  place: number;
  /** The last rated matches, newest first, at most 25. */
  history: LadderMatch[];
}

/** One line of the ladder list. */
export interface LadderRow {
  place: number;
  accountId: string;
  name: string;
  /** The account's banner, 1 to 5, or 0 before the first roll. */
  faction: number;
  ladder: number;
  matches: number;
  /** The row is the reader's own. */
  self: boolean;
}

/** The reader's own line beside the list. */
export interface LadderMe {
  /** 0 while the list does not show the reader. */
  place: number;
  ladder: number;
  rated: boolean;
  guest: boolean;
}

/** The top of the ladder. */
export interface LadderList {
  /** Unix seconds, wall clock. */
  takenAt: number;
  /** The list shows an account whose last rated match is at most this
   *  many days old. */
  activeDays: number;
  rows: LadderRow[];
  /** The count of the accounts of the whole list in each ladder tier,
   *  the lowest tier first (arcade.md §18.4, R1619). */
  tiers: number[];
  /** The reader's own line, or null without a session. */
  me: LadderMe | null;
}

/** The wire shape of a card, where every field may be absent. */
type LadderCardWire = Partial<Omit<LadderCard, 'history'>> & {
  history?: Partial<LadderMatch>[];
};

function readLadderCard(out: LadderCardWire): LadderCard {
  return {
    accountId: out.accountId ?? '',
    name: out.name ?? '',
    guest: out.guest ?? false,
    rated: out.rated ?? false,
    rating: out.rating ?? 0,
    deviation: out.deviation ?? 0,
    volatility: out.volatility ?? 0,
    ladder: out.ladder ?? 0,
    best: out.best ?? 0,
    matches: out.matches ?? 0,
    wins: out.wins ?? 0,
    losses: out.losses ?? 0,
    draws: out.draws ?? 0,
    lastAt: out.lastAt ?? 0,
    place: out.place ?? 0,
    history: (out.history ?? []).map((h) => ({
      at: h.at ?? 0,
      category: h.category ?? '',
      mapId: h.mapId ?? '',
      code: h.code ?? '',
      outcome: h.outcome ?? 'lost',
      before: h.before ?? 0,
      after: h.after ?? 0,
    })),
  };
}

/** The caller's own ladder card. It needs a session; a guest reads one
 *  that says it is a guest. */
export async function getMyLadder(): Promise<LadderCard> {
  return readLadderCard(await call<LadderCardWire>('GET', '/api/arcade/ladder/me'));
}

/** The top of the ladder, highest ladder number first. With a session the
 *  reader's own row is marked and its own line is in `me`. */
export async function getLadderList(limit?: number): Promise<LadderList> {
  const out = await call<{
    takenAt?: number; activeDays?: number;
    rows?: Partial<LadderRow>[]; tiers?: number[]; me?: Partial<LadderMe>;
  }>('GET', withQuery('/api/arcade/ladder', { limit }));
  return {
    takenAt: out.takenAt ?? 0,
    activeDays: out.activeDays ?? 0,
    tiers: out.tiers ?? [],
    rows: (out.rows ?? []).map((r) => ({
      place: r.place ?? 0,
      accountId: r.accountId ?? '',
      name: r.name ?? '',
      faction: r.faction ?? 0,
      ladder: r.ladder ?? 0,
      matches: r.matches ?? 0,
      self: r.self ?? false,
    })),
    me: out.me ? {
      place: out.me.place ?? 0,
      ladder: out.me.ladder ?? 0,
      rated: out.me.rated ?? false,
      guest: out.me.guest ?? false,
    } : null,
  };
}

// ---------------------------------------------------------------------------
// Community maps (community-maps.md §6, R1971-R1975). One block for the
// lobby and the map designer, so the two pages read one shape.
// ---------------------------------------------------------------------------

/** The author of a community map. */
export interface CommunityAuthor {
  publicId: string;
  name: string;
  /** The tag of the author's clan, or '' for an author in no clan. */
  clanTag: string;
}

/** One row of the COMMUNITY list (community-maps.md §6.2). */
export interface CommunityMapRow {
  /** The map code, six characters in capitals (§2.2). */
  code: string;
  name: string;
  author: CommunityAuthor;
  /** The core category folder the map plays in. */
  category: string;
  teamSize: number;
  seats: number;
  /** The latest revision. HOST plays it (§5.2). */
  revision: number;
  likes: number;
  /** The viewer likes the map. False for a viewer with no session. */
  liked: boolean;
  plays: number;
  /** The share of plays that ended in a win or a draw, 0 to 1. */
  completedShare: number;
  publishedAt: number;
  updatedAt: number;
  /** An operator promoted the map into the PLAY board (§5.3). */
  promoted: boolean;
  /** The revision the PLAY lane plays, or 0 for a map not promoted. */
  pinnedRevision: number;
  featured: boolean;
}

/** The counts the COMMUNITY controls state (§6.2). */
export interface CommunityCounts {
  /** One count per filter: all, mine, clan, favourites. */
  filters: Record<CommunityFilter, number>;
  /** One count per category folder under the current filter. */
  categories: Record<string, number>;
}

export type CommunitySort = 'recent' | 'played' | 'liked';
export type CommunityFilter = 'all' | 'mine' | 'clan' | 'favourites';

export interface CommunityPage {
  maps: CommunityMapRow[];
  counts: CommunityCounts;
  /** The cursor of the next page, or '' after the last page. */
  next: string;
}

/** One revision of a map, as MY MAPS and the map card list it (§9.3). */
export interface CommunityRevision {
  revision: number;
  /** The SHA-256 of the canonical bytes, lowercase hex. */
  sha256: string;
  bytes: number;
  note: string;
  plays: number;
  createdAt: number;
}

/** One map card (`GET /api/community/maps/{code}`), and one MY MAPS map. */
export interface CommunityMapCard extends CommunityMapRow {
  description: string;
  completed: number;
  /** The average match length in seconds, or 0 with no play yet. */
  averageSec: number;
  /** The plays of the last 7 days. */
  plays7d: number;
  /** The wins of each team, team 1 first; empty for a map of one team. */
  teamWins: number[];
  /** `published`, or `taken_down` after an operator's takedown (§3.4). */
  status: 'published' | 'taken_down';
  /** Every revision the store keeps, the newest first. MY MAPS only. */
  revisions?: CommunityRevision[];
}

/** `GET /api/community/config` (§6.1). */
export interface CommunityConfig {
  /** The switch `community_maps` of the meta server (§8). */
  enabled: boolean;
  slots: number;
  maxBytes: number;
  /** The session holds the key `map_publisher`. False with no session. */
  canPublish: boolean;
  /** The map slots the session's account uses. 0 with no session. */
  used: number;
}

/** One map slot of `GET /api/community/mine`: a map, or null when empty. */
export interface CommunitySlot {
  slot: number;
  map: CommunityMapCard | null;
}

/** The answer of a publish (§6.3). */
export interface CommunityPublished {
  code: string;
  revision: number;
  sha256: string;
  bytes: number;
  /** False when the bytes matched the latest revision: no new revision. */
  changed: boolean;
}

/** What a publish carries (§3.2). */
export interface CommunityPublish {
  /** The map file as the designer writes it. */
  map: unknown;
  /** The map code the document came from, or '' for a new map. */
  code: string;
  /** A replace of another map in this slot, asked twice by the page. */
  replace: boolean;
  /** One optional line of what changed. */
  note: string;
}

/** The switch, the slot count and what the session may do. No session needed. */
export function getCommunityConfig(): Promise<CommunityConfig> {
  return call<CommunityConfig>('GET', '/api/community/config');
}

/** One page of the COMMUNITY list. `mine`, `clan` and `favourites` need a session. */
export function listCommunityMaps(q: {
  category?: string; sort?: CommunitySort; filter?: CommunityFilter; cursor?: string;
} = {}): Promise<CommunityPage> {
  return call<CommunityPage>('GET', withQuery('/api/community/maps',
    { category: q.category, sort: q.sort, filter: q.filter, cursor: q.cursor }));
}

/** One map card with its figures. */
export function getCommunityMap(code: string): Promise<CommunityMapCard> {
  return call<CommunityMapCard>('GET', `/api/community/maps/${encodeURIComponent(code)}`);
}

/** The map file of the latest revision, as the store keeps it (§2.3). */
export function getCommunityMapDef(code: string): Promise<unknown> {
  return call<unknown>('GET', `/api/community/maps/${encodeURIComponent(code)}/def`);
}

/** The map slots of the session's account, slot 1 first. */
export async function getMyCommunityMaps(): Promise<CommunitySlot[]> {
  const out = await call<{ slots?: CommunitySlot[] }>('GET', '/api/community/mine');
  return out.slots ?? [];
}

/** Publish into one map slot (§3.2). Needs the key `map_publisher`. */
export function publishCommunityMap(slot: number, body: CommunityPublish): Promise<CommunityPublished> {
  return call<CommunityPublished>('PUT', `/api/community/maps/slot/${slot}`, body);
}

/** Unpublish the map of one slot: the map and its revisions go (§3.4). */
export function unpublishCommunityMap(slot: number): Promise<{ ok: boolean }> {
  return call<{ ok: boolean }>('DELETE', `/api/community/maps/slot/${slot}`);
}

/** Like or unlike a map. Both are idempotent; the answer is the new count. */
export function likeCommunityMap(code: string, like: boolean): Promise<{ likes: number; liked: boolean }> {
  return call<{ likes: number; liked: boolean }>(like ? 'PUT' : 'DELETE',
    `/api/community/maps/${encodeURIComponent(code)}/like`, undefined, { idempotent: true });
}
